/**
 * Sessions Section Controller
 * Implementation for the Search page — a federated search/browse surface over
 * Sessions, Library (placeholder), and partner Notes, rendered as one
 * chronological stream with one search box and one type dropdown.
 *
 * @module sections/SessionsSection
 */

import page from '../vendor/page.js';
import { API } from '../core/api.js';
import { escapeHtml, eventBus } from '../core/utils.js';
import {
  emitContentStarred,
  emitContentArchived,
  emitContentDeleted,
  emitContentViewed,
  emitContentFlagged,
  CONTENT_STARRED,
  CONTENT_ARCHIVED,
  CONTENT_DELETED,
  CONTENT_VIEWED,
  CONTENT_FLAGGED
} from '../core/contentSync.js';
import { notifyArchived, notifyArchiveFailed } from '../utils/archiveFeedback.js';
import { ResultsFilterBar } from '../components/ResultsFilterBar.js';
import { renderEmptyState } from '../components/emptyState.js';
import { OrientationBanner } from '../components/OrientationBanner.js';
import { SessionMessageRenderer } from '../components/SessionMessageRenderer.js';
import {
  searchSources,
  normalizeSessionNote,
  discoveryApi,
  stampContinuationTitles,
  iconIdForItem,
  iconSizeForItem,
  guideCoverHtml
} from '../components/searchSources.js';
import { buildNoteDetailPayload, renderNoteNavCard } from '../components/partnerNotesDrillHelper.js';
import { buildContentsMenu } from '../components/guideDrillHelper.js';
import { renderThreadNavCard, buildThreadDetailPayload } from '../components/threadDrillHelper.js';
import { openPatternInPanel } from '../components/patternDrillHelper.js';
import { renderPracticeNavCard, openPracticeInPanel } from '../components/practiceDrillHelper.js';
import { renderDisclosure, wireDisclosures, expandDisclosure, zoneWrapHtml } from '../components/disclosure.js';
import { renderRowKebab, mountSelectToolbar, RowMenuController } from '../components/optionsMenu.js';
import { launchMemoDiscussAdvice } from '../orchestration/memoEntryLauncher.js';
import { openContentItem } from '../components/contentNavigation.js';
import { modalService } from '../services/ModalService.js';
import { StickyStack } from '../utils/StickyStack.js';
import { formatRelativeDate } from '../utils/timeFormat.js';
import { iconArrowUpRight } from '../utils/icons.js';

/** Right-side chevron shown on row hover (outside select mode). */
const CHEVRON_SVG = `<svg width="19" height="19" viewBox="0 0 16 16" aria-hidden="true"><path d="M6 4L10 8L6 12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" fill="none"/></svg>`;

/** Filled star marker — shared sprite symbol. */
const STAR_SVG = `<svg width="15" height="15" viewBox="0 0 24 24" aria-hidden="true"><use href="#icon-star"/></svg>`;

export class SessionsSection {
  /**
   * URL parameter schema for deep linking
   * Used by URLParamsHandler to parse and validate query parameters
   * Example: /search?session=42 loads Session 42's detail view;
   * /search?note=<id> opens that partner note's detail;
   * /search?guide=<guideDefinitionId> REDIRECTS to the Library Guides tab,
   * which is where a guide reads;
   * /search?type=notes filters the stream to notes.
   * `type` values are plural ('all' is accepted gracefully but the canonical
   * URL omits the param for All — dropdown writes delete `type` on All).
   * `session` and `note` are mutually exclusive detail params (opening one
   * scrubs the other) — see applyURLParams.
   */
  static urlParamSchema = {
    session: {
      type: 'string',
      validate: (v) => /^\d+$/.test(v) && parseInt(v, 10) >= 1
    },
    // Partner-note detail (PartnerMemo UUID) — panel-routed, same as `session`.
    note: {
      type: 'string',
      validate: (v) => typeof v === 'string' && v.length > 0
    },
    // Guide (guideDefinitionId slug) — parsed only so a legacy `/search?guide=`
    // link can be redirected to `/library`. Byte-identical validator to
    // InsightsSection.js's `guide` param, which is where it lands.
    guide: {
      type: 'string',
      validate: (v) => typeof v === 'string' && v.length > 0
    },
    type: {
      type: 'enum',
      enum: ['all', 'sessions', 'insights', 'notes']
    },
    // Star-only filter — present as `starred=1` when active.
    starred: {
      type: 'enum',
      enum: ['1']
    }
  };

  /**
   * Plural URL `?type=` values → singular SearchItem.type. 'all' has no entry
   * (no type filter). `insights` is the umbrella filter label (mirrors the
   * `/library` page naming) — it maps to the single `guide` SearchItem type
   * since guides are the only insights-tab content indexed in search today;
   * if patterns/topics ever become searchable, they'd join under this same
   * `insights` filter rather than getting their own dropdown entries.
   */
  static CONTENT_TYPE_MAP = {
    sessions: 'session',
    insights: 'guide',
    notes: 'note'
  };

  /** Browse-mode (no query) empty-state copy per content type. */
  static BROWSE_EMPTY_COPY = {
    all: {
      title: 'Nothing here yet',
      description: 'Complete a therapy session to get started.'
    },
    sessions: {
      title: 'No session notes yet',
      description: 'Complete a therapy session to see notes here.'
    },
    notes: {
      title: 'No partner notes yet',
      description: 'Notes your partner shares with you will appear here.'
    },
    insights: {
      title: 'No guides yet',
      description: 'Guides are personalized resources created from your discovery insights.'
    }
  };

  /** Query-active empty-state noun per content type. */
  static SEARCH_EMPTY_NOUNS = {
    all: 'results',
    sessions: 'sessions',
    notes: 'notes',
    insights: 'guides'
  };

  /**
   * Search-box placeholder per content type. The placeholder communicates the
   * active content filter (whether the user picked it or it was autoselected on
   * entry); 'all' stays a bare "Search...".
   */
  static SEARCH_PLACEHOLDERS = {
    all: 'Search...',
    sessions: 'Search sessions...',
    notes: 'Search notes...',
    insights: 'Search insights...'
  };

  /**
   * @param {Object} elements - DOM element references
   * @param {HTMLElement} elements.container - Section container element
   * @param {EventBus} eventBus - Event bus for module communication
   */
  constructor(elements, eventBus) {
    this.container = elements.container;
    this.eventBus = eventBus;

    // State
    this.isActive = false;

    // Federated stream state
    this.items = [];                  // Merged SearchItems from all sources
    this.filteredItems = [];          // After search/type/star filter
    this.sessionSearchQuery = '';     // Current search text
    this.contentType = 'all';         // 'all' | 'sessions' | 'insights' | 'notes' (URL-plural values)
    this.starredFilter = false;       // star-only filter
    this.isLoadingSessions = false;
    this.sessionLoadError = null;     // All sources failed
    this.sourceErrors = [];           // Labels of individually failed sources
    this.actionNotice = '';           // Human bulk-action failure notice
    this.filterBar = null;

    // Select mode (multi-select bulk actions)
    this.selectMode = false;
    this.selectedKeys = new Set();    // "type:id" keys of selected cards
    /** Row options menu open/close controller (menu is a child of its `.session-card`). */
    this._rowMenu = new RowMenuController({
      container: () => this.container,
      keyAttr: 'data-item-key',
      rowSelector: '.session-card',
      hoverClose: true,
    });
    this._actionInFlight = false;     // disables toolbar/kebab while a batch runs

    // Detail state — the session and note panels are mutually exclusive (only
    // one detail is open at a time; opening one clears the other).
    this.currentSessionNote = null;   // open session-detail (SearchItem payload)
    this.currentDetailNote = null;    // open note-detail (the note SearchItem)
    this._panelOpen = false;

    // Orientation banner
    this.orientationBanner = null;

    this.initialize();
  }

  /**
   * Initialize section
   */
  initialize() {
    eventBus.on('right-panel:closed', (data) => {
      // Five routed content types are ours: session-detail, note-detail,
      // transcript (the drilled sub-view of session-detail — closing directly
      // from that layer emits its own contentType, not the parent's),
      // library-pattern (the pattern detail session detail's "See pattern"
      // drills into — the EXACT literal InsightsSection.js already emits, so
      // a panel opened from either page is recognized as closeable by
      // whichever section is active, and like transcript it is a drilled
      // layer whose direct close names itself, not its parent), and
      // practice-detail (the experiment detail the Experiments zone drills
      // into — same literal PracticesSection emits, same drilled-layer reason;
      // exactly one section is ever active, so the two handlers can't both run).
      const ct = data?.contentType;
      const isOurPanel = ct === 'session-detail' || ct === 'note-detail' || ct === 'transcript'
        || ct === 'library-pattern' || ct === 'practice-detail';
      if (!isOurPanel || !this.isActive) return;

      this._clearDetailSelection();
      this._panelOpen = false;
      this.updateSessionsList();

      // Clean up the URL when the panel closed via × or any other in-section path.
      // Skipped when isActive is false (cross-section nav owns its own URL change).
      // All three detail params are scrubbed (only one is ever present).
      const url = new URL(window.location.href);
      let changed = false;
      if (url.searchParams.has('session')) { url.searchParams.delete('session'); changed = true; }
      if (url.searchParams.has('note')) { url.searchParams.delete('note'); changed = true; }
      if (url.searchParams.has('guide')) { url.searchParams.delete('guide'); changed = true; }
      if (changed) {
        page.replace(url.pathname + (url.search ? url.search : ''));
      }
    });

    // Universal cross-surface sync bridge (contentSync): star/archive/delete
    // from any surface (sidebar row, /notes, /discovery Open Threads, or this
    // section's own bulk paths) keep /search's list + open detail in sync.
    // /search holds only `session` + `note` items; `thread` events find no
    // item and self-skip. Guarded with `isActive` at the top (review fix #4):
    // an inactive /search must not fire `right-panel:close` over another
    // section's open panel, and `activate()` reloads fresh data anyway.
    eventBus.on(CONTENT_STARRED, ({ itemType, itemId, starred }) => {
      if (!this.isActive) return;
      const now = starred ? new Date().toISOString() : null;
      // Patch open-detail state + flip the visible panel header FIRST, before
      // any list early-return (an item open-but-absent from the list still
      // updates its detail).
      let panelMatch = false;
      if (itemType === 'session' && this.currentSessionNote
          && String(this.currentSessionNote.id) === String(itemId)) {
        this.currentSessionNote = { ...this.currentSessionNote, starredAt: now };
        panelMatch = true;
      } else if (itemType === 'note' && this.currentDetailNote
          && String(this.currentDetailNote.id) === String(itemId)) {
        this.currentDetailNote.starred = starred;
        if (this.currentDetailNote.payload) this.currentDetailNote.payload.starredAt = now;
        panelMatch = true;
      }
      if (panelMatch && this._panelOpen) eventBus.emit('right-panel:set-star', { starred });

      const item = this.items.find(it => it.type === itemType && String(it.id) === String(itemId));
      if (!item || item.starred === starred) return;
      item.starred = starred;
      if (item.payload) item.payload.starredAt = now;
      this.applySessionFilters();
    });

    eventBus.on(CONTENT_ARCHIVED, ({ itemType, itemId, archived }) => {
      if (!this.isActive) return;
      const now = archived ? new Date().toISOString() : null;
      if (itemType === 'session' && this.currentSessionNote
          && String(this.currentSessionNote.id) === String(itemId)) {
        this.currentSessionNote = { ...this.currentSessionNote, archivedAt: now };
      } else if (itemType === 'note' && this.currentDetailNote
          && String(this.currentDetailNote.id) === String(itemId)) {
        this.currentDetailNote.archived = archived;
        if (this.currentDetailNote.payload) this.currentDetailNote.payload.archivedAt = now;
      }
      const item = this.items.find(it => it.type === itemType && String(it.id) === String(itemId));
      if (!item || item.archived === archived) return;
      item.archived = archived;
      if (item.payload) item.payload.archivedAt = now;
      this.applySessionFilters();
    });

    eventBus.on(CONTENT_DELETED, ({ itemType, itemId }) => {
      if (!this.isActive) return;
      const targetKey = `${itemType}:${itemId}`;
      const panelKey = this.currentSessionNote
        ? `session:${this.currentSessionNote.id}`
        : (this.currentDetailNote ? SessionsSection.itemKey(this.currentDetailNote) : null);
      const before = this.items.length;
      this.items = this.items.filter(it => !(it.type === itemType && String(it.id) === String(itemId)));
      // `_runBulk`'s own delete path already removes the row from `this.items`
      // BEFORE broadcasting — so a self-originated delete reaches this handler
      // with nothing left to remove. Bail out entirely (not just skip the
      // refresh) so a self-originated delete doesn't also emit a redundant
      // second `right-panel:close` alongside `_runBulk`'s own direct emit.
      if (this.items.length === before) return;
      if (panelKey === targetKey && this._panelOpen) eventBus.emit('right-panel:close');
      this.applySessionFilters();
    });

    // Passive dot sync: /search never toggles viewed/flagged state of its own
    // (no new kebab items — see search-notes-dots-and-practices-sidebar.md's
    // locked decision), but its own rows' dots must not go stale if the
    // sidebar (or another surface) changes state while this page is open in
    // the same tab. No API call here — just re-derive from the bridged flag.
    eventBus.on(CONTENT_VIEWED, (data = {}) => {
      const { itemType, itemId, viewed } = data || {};
      if (!this.isActive) return;
      if (!['session', 'note', 'guide'].includes(itemType)
          || !itemId || (viewed !== true && viewed !== false)) return;

      const viewedAt = viewed ? new Date().toISOString() : null;
      const matchingItems = new Set([
        ...this.items.filter(it => it.type === itemType && String(it.id) === String(itemId)),
        ...this.filteredItems.filter(it => it.type === itemType && String(it.id) === String(itemId))
      ]);
      let changed = false;

      for (const item of matchingItems) {
        if (item.viewed !== viewed || Boolean(item.payload?.viewedAt) !== viewed) {
          item.viewed = viewed;
          if (item.payload) item.payload.viewedAt = viewedAt;
          changed = true;
        }
      }

      if (itemType === 'note' && this.currentDetailNote
          && String(this.currentDetailNote.id) === String(itemId)
          && (this.currentDetailNote.viewed !== viewed
            || Boolean(this.currentDetailNote.payload?.viewedAt) !== viewed)) {
        this.currentDetailNote.viewed = viewed;
        if (this.currentDetailNote.payload) this.currentDetailNote.payload.viewedAt = viewedAt;
        changed = true;
      }

      if (changed) this.applySessionFilters();
    });

    eventBus.on(CONTENT_FLAGGED, ({ itemType, itemId, flagged }) => {
      if (!this.isActive) return;
      const item = this.items.find(it => it.type === itemType && String(it.id) === String(itemId));
      if (!item || item.flagged === flagged) return;
      item.flagged = flagged;
      if (item.payload) {
        item.payload.flaggedAt = flagged ? new Date().toISOString() : null;
      }
      if (this.currentSessionNote
          && String(this.currentSessionNote.id) === String(itemId)) {
        this.currentSessionNote.flaggedAt = item.payload?.flaggedAt || null;
      }
      this.applySessionFilters();
    });
  }

  /**
   * Forget which detail (session, note, or guide) is open. Does NOT touch
   * `_panelOpen` or the URL — callers own those. Shared by the panel-closed
   * handler, `clearActiveDetailSelection` and `deactivate()`, so a card
   * highlight can never outlive its panel across a page change.
   * @private
   */
  _clearDetailSelection() {
    this.currentSessionNote = null;
    this.currentDetailNote = null;
  }

  /**
   * Clear the routed detail selection (session, note, or guide) while leaving
   * the panel open. Used when a non-routed panel replaces the current detail.
   */
  clearActiveDetailSelection() {
    this._clearDetailSelection();
    this.updateSessionsList();

    const url = new URL(window.location.href);
    let changed = false;
    if (url.searchParams.has('session')) { url.searchParams.delete('session'); changed = true; }
    if (url.searchParams.has('note')) { url.searchParams.delete('note'); changed = true; }
    if (url.searchParams.has('guide')) { url.searchParams.delete('guide'); changed = true; }
    if (changed) {
      page.replace(url.pathname + (url.search ? url.search : ''));
    }
  }

  /**
   * Activate this section (when panel opens to this section)
   *
   * Idempotent self-guard: route handlers re-fire on URL changes (e.g. card
   * click pushes ?session=N), and we don't want to reload + re-render the
   * list every time the panel opens or closes within the section.
   */
  async activate() {
    if (this.isActive) return;
    this.isActive = true;
    await this.loadData();
    if (!this.isActive) return;
    this.render();
  }

  /**
   * Deactivate this section (when switching to another section)
   */
  deactivate() {
    this.isActive = false;

    // Release the delegated action handler + any open row menu / select state.
    this.container.removeEventListener('click', this.handleActionClick);
    this.container.removeEventListener('contextmenu', this.handleRowContextMenu);
    this._rowMenu.close();
    this._resetSelectMode();

    // Clean up orientation banner
    if (this.orientationBanner) {
      this.orientationBanner.destroy();
      this.orientationBanner = null;
    }

    // Clean up filter bar
    if (this.filterBar) {
      this.filterBar.destroy();
      this.filterBar = null;
    }

    // Clean up the sticky header stack
    this.stickyStack?.destroy();
    this.stickyStack = null;

    if (this._panelOpen) {
      eventBus.emit('right-panel:close');
      this._panelOpen = false;
    }
    // The cross-page close's `right-panel:closed` lands after `isActive` is
    // already false, so the closed handler (rightly) ignores it and never
    // clears the selection. Clear it here, or the next `render()` on return
    // paints the old card active with no panel behind it.
    this._clearDetailSelection();
  }

  /**
   * Apply URL parameters — drives panel open/close from URL state.
   *
   * Bidirectional, over three mutually exclusive detail params (`session` /
   * `note` / `guide` — only one is ever present):
   *  - `params.session` present → open or swap to that session detail
   *  - `params.note` present → open or swap to that partner-note detail
   *  - `params.guide` present → open or swap to that guide's detail
   *  - all three absent + a routed detail panel open → close
   *
   * Page-info detail (contentType 'session-info') is intentionally untouched —
   * it isn't URL-routed and is closed via the × button only.
   *
   * The `type` filter is handled independently of the detail params — a URL
   * like `/search?type=notes&session=3` shows the notes list AND opens the
   * session panel (the panel is routed regardless of whether the type filter
   * hides that card from the visible list).
   *
   * @param {Object} params - Parsed URL parameters
   * @param {string} [params.session] - Session number to load directly
   * @param {string} [params.note] - Partner-note id to open directly
   * @param {string} [params.guide] - Guide definition id to open directly
   * @param {string} [params.type] - Content type filter (plural URL value)
   */
  async applyURLParams(params) {
    // Absent → 'all' reset is explicit: URLParamsHandler.parse never applies
    // defaults and section state persists across reactivation (browser back
    // from ?type=notes to bare /search must reset). The equality guard
    // prevents re-entry churn — every panel open/close page.replace re-fires
    // the route handler.
    // type / starred both follow the absent→default reset + equality guard
    // pattern (URLParamsHandler.parse never applies defaults; section state
    // persists across reactivation). Either changing exits select mode and
    // re-derives the visible list.
    const nextType = params.type || 'all';
    const nextStarred = params.starred === '1';
    let filtersChanged = false;

    if (nextType !== this.contentType) {
      this.contentType = nextType;
      // setFilterValue doesn't fire onChange — safe (no write-back loop).
      this.filterBar?.setFilterValue('contentType', nextType);
      filtersChanged = true;
    }
    if (nextStarred !== this.starredFilter) {
      this.starredFilter = nextStarred;
      this.filterBar?.setActionPressed('starred', nextStarred);
      filtersChanged = true;
    }
    if (filtersChanged) {
      // Any filter/URL change exits select mode + clears selection.
      if (this.selectMode) {
        this._resetSelectMode();
        this._mountFilterBar();
      }
      this.applySessionFilters();
    }

    if (params.session) {
      const sessionNumber = parseInt(params.session, 10);
      // Dedup: same session already shown in panel
      if (this._panelOpen
          && this.currentSessionNote?.session?.sessionNumber === sessionNumber) {
        return;
      }
      await this.loadAndShowSessionByNumber(sessionNumber);
    } else if (params.note) {
      // Dedup: same note already shown in panel
      if (this._panelOpen
          && this.currentDetailNote
          && String(this.currentDetailNote.id) === String(params.note)) {
        return;
      }
      this.showNoteByIdFromParam(params.note);
    } else if (params.guide) {
      // A guide reads on `/library` only (see handleGuideCardClick). The param
      // survives in the schema for the links already in the wild — bookmarks,
      // and any history entry written before the reader moved — which redirect
      // rather than 404. `replace`, so Back does not land on the redirecting
      // URL and bounce forward again.
      openContentItem({ type: 'guide', id: params.guide }, { replace: true });
    } else if (this._panelOpen && (this.currentSessionNote || this.currentDetailNote)) {
      eventBus.emit('right-panel:close');
    }
  }

  /**
   * Load session notes by session number and show detail view.
   * Driven by `applyURLParams` whenever the URL carries `?session=N`.
   * @param {number} sessionNumber - Session number (1, 2, 3, etc.)
   */
  async loadAndShowSessionByNumber(sessionNumber) {
    try {
      const result = await API.getSessionNotesByNumber(sessionNumber);

      // Race guards: navigated away, or URL changed to a different session,
      // while the fetch was in flight.
      if (!this.isActive) return;
      const wantedNow = parseInt(
        new URLSearchParams(window.location.search).get('session') || '',
        10
      );
      if (wantedNow !== sessionNumber) return;

      if (!result || result.error || result.success === false || !result.session) {
        console.warn('[SessionsSection] Invalid session result for number:', sessionNumber);
        // Scrub the bad ?session=... param from the URL (preserving ?type=)
        const url = new URL(window.location.href);
        url.searchParams.delete('session');
        page.replace(url.pathname + (url.search ? url.search : ''));
        window.app?.modules?.rightPanelManager?.closeStaleHandoff();
        return;
      }
      this.showDetailView(result);
      this._scrollActiveCardIntoView();
    } catch (error) {
      console.error('[SessionsSection] Error loading session by number:', error);
      window.app?.modules?.rightPanelManager?.closeStaleHandoff();
    }
  }

  /**
   * Bring the just-opened session's highlighted card into view within the
   * results list, near the top. Called after a `?session=N` open — which
   * covers BOTH an in-list card click (routes through here via URL) and an
   * open originating outside the visible list (sidebar recents link, or a
   * fresh `/search?session=N` load).
   *
   * Every open repositions the card (no "already visible" short-circuit): the
   * requirement is that any click — including re-clicking a session whose card
   * scrolled up near the top — brings that card back to the top of the list.
   *
   * The `.search-sticky-bar` (search input + results bar) is pinned at the top
   * of this same scroll container and overlays it, so "the top of the list" for
   * a card means just BELOW that bar, not the raw container top — hence the
   * sticky-height offset. `scrollTo` clamps to the list's max scroll, so a card
   * near the bottom still comes as high as the content allows. No-ops when no
   * card is rendered (e.g. a `type` filter hides it from the visible list even
   * though the panel is still routed open).
   */
  _scrollActiveCardIntoView() {
    // rAF: let the just-replaced list markup + the right-panel open settle so
    // getBoundingClientRect reflects post-open layout.
    requestAnimationFrame(() => {
      if (!this.isActive) return;
      const list = this.container?.querySelector('.explore-view--list');
      const card = this.container?.querySelector('.session-card--active');
      if (!list || !card) return;

      // Measure from the BOTTOM of the sticky search + results bar (itself
      // pinned below the sticky "Search" title), so the card lands below the
      // whole pinned stack rather than under it. Falls back to the list top if
      // the bar is absent (loading / error states).
      const stickyBar = this.container.querySelector('.search-sticky-bar');
      const stickyBottom = stickyBar
        ? stickyBar.getBoundingClientRect().bottom
        : list.getBoundingClientRect().top;
      // Small gap below the sticky bar so the card isn't flush against it.
      const TOP_GAP = 12;

      const cardRect = card.getBoundingClientRect();
      const delta = cardRect.top - stickyBottom - TOP_GAP;
      list.scrollTo({ top: list.scrollTop + delta, behavior: 'smooth' });
    });
  }

  /**
   * Load the federated stream: every source in parallel via
   * Promise.allSettled. The adapter contract makes rejection the single
   * failure signal (each `load()` validates its result and throws), so this
   * method only branches on settled status:
   *  - some sources fail → `sourceErrors` drives a combined inline notice
   *  - ALL sources fail → `sessionLoadError` drives the full error + Retry
   * Items with null/invalid dates are dropped from the stream (an invalid
   * date month-keys to "NaN-NaN" and sorts first — never let it in).
   */
  async loadData() {
    this.isLoadingSessions = true;
    this.sessionLoadError = null;
    this.sourceErrors = [];

    const ctx = { stampContinuationTitles };
    const results = await Promise.allSettled(searchSources.map(source => source.load(ctx)));

    const items = [];
    const failed = [];
    results.forEach((result, i) => {
      const source = searchSources[i];
      if (result.status === 'fulfilled') {
        items.push(...source.normalize(result.value));
      } else {
        console.error(`[SessionsSection] Failed to load ${source.label}:`, result.reason);
        failed.push(source.label);
      }
    });

    if (failed.length === searchSources.length) {
      this.sessionLoadError = 'All search sources failed to load';
      this.items = [];
      this.filteredItems = [];
    } else {
      this.sourceErrors = failed;
      this.items = items.filter(item =>
        item.date && !Number.isNaN(new Date(item.date).getTime())
      );
      this.applySessionFilters();
    }

    this.isLoadingSessions = false;
  }

  /**
   * Render complete interface
   */
  render() {
    this.container.innerHTML = `
      <div class="explore-section">
        <div class="explore-container">
          <div class="explore-view--list" data-sticky-stack>
            <h2 class="page-header__title content-width" data-sticky-slice>History</h2>

            <div class="explore-content content-width">
              ${this.renderSessionsContent()}
            </div>
          </div>
        </div>
      </div>
    `;

    this._mountFilterBar();
    this.attachEventListeners();
    this._showOrientationBanner();
    this._initStickyStack();
  }

  static ORIENTATION_MESSAGE = 'This is your therapy record — every session and partner note in one place. Browse the stream or search to return to earlier work and track your progress over time.';

  _showOrientationBanner() {
    if (this.orientationBanner) {
      this.orientationBanner.destroy();
      this.orientationBanner = null;
    }
    // Anchor above the sticky search bar (the page title was removed) so the
    // intro scrolls away rather than pinning. Absent in the loading/error
    // states — the intro simply waits for the loaded stream.
    const anchor = this.container.querySelector('.search-sticky-bar');
    if (!anchor) return;
    this.orientationBanner = new OrientationBanner({
      container: anchor,
      key: 'sessions',
      message: SessionsSection.ORIENTATION_MESSAGE,
      insertPosition: 'beforebegin',
    });
    this.orientationBanner.show();
  }

  /**
   * Render sessions content
   * @returns {string} HTML string
   */
  renderSessionsContent() {
    // Loading state
    if (this.isLoadingSessions) {
      return `
        <div class="explore-tab-content" data-tab-content="sessions">
          <div class="explore-loading">
            <p>Loading...</p>
          </div>
        </div>
      `;
    }

    // Error state (all sources failed)
    if (this.sessionLoadError) {
      return `
        <div class="explore-tab-content" data-tab-content="sessions">
          <div class="explore-error">
            <p>Failed to load results. Please try again.</p>
            <button class="btn btn--secondary" data-action="retry-sessions">Retry</button>
          </div>
        </div>
      `;
    }

    // Render search bar, results bar, and cards
    const clearVisibleClass = this.sessionSearchQuery ? 'insights-search__clear--visible' : '';

    return `
      <div class="explore-tab-content" data-tab-content="sessions">
        <!-- Sticky search + results bar (slice 2: pins below the page title) -->
        <div class="search-sticky-bar" data-sticky-slice>
          <!-- Search Bar (reuses insights-search pattern) -->
          <div class="insights-search">
            <div class="insights-search__icon">
              <svg width="25" height="25" aria-hidden="true">
                <use href="#icon-search"/>
              </svg>
            </div>
            <input type="text"
                   class="insights-search__input"
                   placeholder="${this.escapeHtml(this._searchPlaceholder())}"
                   value="${this.escapeHtml(this.sessionSearchQuery)}">
            <button class="insights-search__clear ${clearVisibleClass}" data-action="clear-session-search">
              <svg width="22" height="22">
                <use href="#icon-close"/>
              </svg>
            </button>
          </div>

          <!-- Results Bar -->
          ${this.renderResultsBar()}
        </div>

        <!-- Session Cards List -->
        <div class="session-cards-container">
          ${this.renderSessionCardsList()}
        </div>
      </div>
    `;
  }

  /**
   * (Re)bind the sticky header stack: the "History" title, then
   * `.search-sticky-bar` (search input + results bar) pinned directly below it.
   * Idempotent — render() replaces `container.innerHTML`, so the scroll root is
   * a new node every time and the previous controller must be released first.
   */
  _initStickyStack() {
    this.stickyStack?.destroy();
    this.stickyStack = null;
    const root = this.container.querySelector('.explore-view--list');
    if (root) this.stickyStack = new StickyStack(root);
  }

  /**
   * Render the results-bar mount. ResultsFilterBar populates it after the
   * page shell is in the DOM via _mountFilterBar().
   * @returns {string} HTML string
   */
  renderResultsBar() {
    return `<div data-filter-bar-mount="sessions"></div>`;
  }

  /**
   * Mount the bar: the shared ResultsFilterBar (count · ★toggle | type |
   * Select) in normal mode, or the select-mode bulk toolbar.
   */
  _mountFilterBar() {
    const mount = this.container.querySelector('[data-filter-bar-mount="sessions"]');
    if (!mount) return;
    if (this.filterBar) {
      this.filterBar.destroy();
      this.filterBar = null;
    }
    if (this.selectMode) {
      this._renderSelectToolbar(mount);
      return;
    }
    this.filterBar = new ResultsFilterBar({
      container: mount,
      itemLabel: { singular: 'Result', plural: 'Results' },
      count: this.filteredItems.length,
      filters: [
        {
          id: 'contentType',
          label: 'Show',
          defaultValue: this.contentType,
          options: [
            { value: 'all', label: 'All' },
            { value: 'sessions', label: 'Sessions', itemLabel: { singular: 'Session', plural: 'Sessions' } },
            { value: 'insights', label: 'Insights', itemLabel: { singular: 'Guide', plural: 'Guides' } },
            { value: 'notes', label: 'Notes', itemLabel: { singular: 'Note', plural: 'Notes' } }
          ]
        }
      ],
      actions: [
        { id: 'starred', label: 'Starred only', icon: 'star', pressed: this.starredFilter, onClick: () => this._toggleStarredFilter() },
        { id: 'select', label: 'Select', icon: 'select', onClick: () => this._enterSelectMode() }
      ],
      onChange: (state) => this._handleFilterChange(state)
    });
  }

  /**
   * Handle a filter change from the bar. A content-type change writes the URL
   * via page.replace (URL is the source of truth — the route handler re-fires
   * applyURLParams). The canonical URL omits `type` for All; a type change
   * drops any open detail param (`session` / `note`) so an open panel closes.
   * @param {{filters: Object}} state
   */
  _handleFilterChange(state) {
    const nextType = state.filters.contentType;
    const nextStarred = Boolean(state.actions?.starred);
    const typeChanged = nextType !== this.contentType;
    // The starred toggle is URL-backed and section-owned, so a bar-side reset
    // reaches us only through the payload. Both axes are written in ONE
    // page.replace so a reset can't render an intermediate state.
    const starredChanged = nextStarred !== this.starredFilter;

    if (typeChanged || starredChanged) {
      const url = new URL(window.location.href);
      if (nextType === 'all') url.searchParams.delete('type');
      else url.searchParams.set('type', nextType);
      if (nextStarred) url.searchParams.set('starred', '1');
      else url.searchParams.delete('starred');
      url.searchParams.delete('session');
      url.searchParams.delete('note');
      page.replace(url.pathname + (url.search ? url.search : ''));
      return;
    }

    this.updateSessionsList();
  }

  /**
   * Toggle the star-only filter and write it to the URL (`starred=1` present /
   * absent). URL is the source of truth — the route handler re-applies it.
   */
  _toggleStarredFilter() {
    const url = new URL(window.location.href);
    if (this.starredFilter) url.searchParams.delete('starred');
    else url.searchParams.set('starred', '1');
    url.searchParams.delete('session');
    url.searchParams.delete('note');
    page.replace(url.pathname + (url.search ? url.search : ''));
  }

  /**
   * Render the select-mode bulk toolbar in the bar mount: "N selected" on the
   * left; Star/Unstar, Archive/Unarchive, Delete, Cancel on the right. Bulk
   * buttons disable at zero selection or while a batch is in flight.
   * @param {HTMLElement} [mount] - Bar mount (looked up when omitted)
   */
  _renderSelectToolbar(mount = null) {
    const el = mount || this.container.querySelector('[data-filter-bar-mount="sessions"]');
    if (!el) return;
    const n = this.selectedKeys.size;
    const disabled = n === 0 || this._actionInFlight;
    const selected = this._selectedItems();
    const allStarred = n > 0 && selected.every((it) => it.starred);
    // No view axis — the label toggles on the selection: all-archived →
    // Unarchive, otherwise Archive (mirrors the star toggle).
    const allArchived = n > 0 && selected.every((it) => it.archived);
    mountSelectToolbar(el, {
      count: n,
      actions: [
        { action: 'select-star', label: allStarred ? 'Unstar' : 'Star', icon: allStarred ? 'star' : 'star-outline', disabled },
        { action: 'select-archive', label: allArchived ? 'Unarchive' : 'Archive', icon: allArchived ? 'unarchive' : 'archive', disabled },
        { action: 'select-delete', label: 'Delete', icon: 'trash', disabled },
        { action: 'select-cancel', label: 'Cancel', icon: 'close' }
      ]
    });
  }

  /**
   * Render the combined per-source failure notice (empty string when all
   * sources loaded). Lives inside the region updateSessionsList rebuilds
   * (.session-cards-container) so it clears/re-renders correctly.
   * @returns {string} HTML string
   */
  renderSourceErrorsNotice() {
    const parts = [];
    if (this.sourceErrors.length) {
      parts.push(`Couldn't load ${this.escapeHtml(this.sourceErrors.join(' and '))}.`);
    }
    if (this.actionNotice) {
      parts.push(this.escapeHtml(this.actionNotice));
    }
    if (!parts.length) return '';
    return `<div class="session-cards-notice">${parts.join(' ')}</div>`;
  }

  /**
   * Pick the empty-state copy for the current state: an active search, a
   * starred-only filter, or a genuinely empty collection.
   *
   * The search variant deliberately doesn't echo the query — `sessionSearchQuery`
   * is lowercased on input, so quoting it back would misrender the user's own
   * capitalisation.
   * @returns {{title: string, description: string}}
   */
  _emptyStateCopy() {
    if (this.sessionSearchQuery) {
      const noun = SessionsSection.SEARCH_EMPTY_NOUNS[this.contentType] || 'results';
      return {
        title: `No ${noun} match your search`,
        description: 'Try a different search term, or clear the search to see everything.'
      };
    }
    if (this.starredFilter) {
      return {
        filtered: !this.selectMode,
        title: 'No starred items',
        description: 'Star a session, note, or guide to keep it handy here.'
      };
    }
    const copy = SessionsSection.BROWSE_EMPTY_COPY[this.contentType]
      || SessionsSection.BROWSE_EMPTY_COPY.all;
    // A non-default content type is a filter the user can undo; "all" being
    // empty is a genuine absence of data, so no reset is offered there.
    return this.contentType !== 'all' && !this.selectMode
      ? { ...copy, filtered: true }
      : copy;
  }

  /**
   * Render item cards grouped by month
   * @returns {string} HTML string
   */
  renderSessionCardsList() {
    const noticeHtml = this.renderSourceErrorsNotice();

    // Empty state. The notice is preserved either way — dropping it would hide
    // a partial source-load failure behind a "nothing here" message.
    if (this.filteredItems.length === 0) {
      return noticeHtml + renderEmptyState(this._emptyStateCopy());
    }

    // Group by month
    const groups = this.groupSessionsByMonth(this.filteredItems);
    const activeId = this.currentSessionNote?.session?.id
      ?? this.currentSessionNote?.sessionId
      ?? null;
    // A note detail is keyed by (type, id) — ids collide across sources, so an
    // id-only match could highlight the wrong card.
    const activeKey = this.currentDetailNote
      ? SessionsSection.itemKey(this.currentDetailNote)
      : null;

    // Render groups
    return noticeHtml + groups.map(group => `
      <div class="session-month-group">
        <div class="session-month-group__cards">
          ${group.items.map(item => SessionsSection.renderItemCard(item, {
            activeId,
            activeKey,
            query: this.sessionSearchQuery,
            selectMode: this.selectMode,
            selected: this.selectedKeys.has(SessionsSection.itemKey(item))
          })).join('')}
        </div>
      </div>
    `).join('');
  }

  /**
   * Stable per-item selection key — identity across the page is the (type, id)
   * pair (ids can collide across sources).
   * @param {Object} item - SearchItem
   * @returns {string} "type:id"
   */
  static itemKey(item) {
    return `${item.type}:${item.id}`;
  }

  /**
   * Group SearchItems by month (via `item.date`; null/invalid dates were
   * dropped at load time)
   * @param {Array} items - SearchItems array
   * @returns {Array} Array of { label, items } objects sorted by month descending
   */
  groupSessionsByMonth(items) {
    const groups = {};

    for (const item of items) {
      const date = new Date(item.date);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const monthLabel = date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

      if (!groups[monthKey]) {
        groups[monthKey] = { label: monthLabel, items: [] };
      }
      groups[monthKey].items.push(item);
    }

    // Sort by month descending
    return Object.entries(groups)
      .sort(([a], [b]) => b.localeCompare(a))
      .map(([_, group]) => group);
  }

  /**
   * Highlight query matches in text, safely: plain `indexOf` scan over the
   * lowercased text (NOT RegExp — queries containing `(`/`[`/`\` must not
   * break), escape-then-wrap so no unescaped user content reaches innerHTML.
   * Output is for ELEMENT CONTENT ONLY, never attributes, because it includes
   * trusted `<strong>` markup. Bolding weight comes from CSS (`font-weight: 600` —
   * Myriad Pro has no 500 face).
   * @param {string} text - Raw text to render
   * @param {string} query - Lowercased search query ('' → escaped text as-is)
   * @returns {string} Escaped HTML with matches wrapped in <strong>
   */
  static highlightMatch(text, query) {
    const raw = String(text ?? '');
    if (!query) return SessionsSection.escapeHtml(raw);

    const q = String(query).toLowerCase();
    const lower = raw.toLowerCase();
    let idx = lower.indexOf(q);
    if (idx === -1) return SessionsSection.escapeHtml(raw);

    let out = '';
    let pos = 0;
    while (idx !== -1) {
      out += SessionsSection.escapeHtml(raw.slice(pos, idx));
      out += `<strong>${SessionsSection.escapeHtml(raw.slice(idx, idx + q.length))}</strong>`;
      pos = idx + q.length;
      idx = lower.indexOf(q, pos);
    }
    out += SessionsSection.escapeHtml(raw.slice(pos));
    return out;
  }

  /**
   * Build the Gemini-style match snippet for a SearchItem: the first matching
   * searchField that isn't the displayed title, windowed to ~100 chars
   * centered on the match, ellipses at cut ends, matches bolded.
   * @param {Object} item - SearchItem
   * @param {string} query - Lowercased search query
   * @returns {string} Snippet HTML, or '' when no non-title field matches
   */
  static buildSnippet(item, query) {
    if (!query) return '';
    const q = String(query).toLowerCase();
    const WINDOW = 100;

    for (const field of item.searchFields || []) {
      if (!field || field === item.title) continue;
      const text = String(field);
      const idx = text.toLowerCase().indexOf(q);
      if (idx === -1) continue;

      let start = Math.max(0, idx - Math.floor((WINDOW - q.length) / 2));
      let end = start + WINDOW;
      if (end > text.length) {
        end = text.length;
        start = Math.max(0, end - WINDOW);
      }
      const prefix = start > 0 ? '…' : '';
      const suffix = end < text.length ? '…' : '';
      return `${prefix}${SessionsSection.highlightMatch(text.slice(start, end), q)}${suffix}`;
    }
    return '';
  }

  /**
   * Render a single stream card for any SearchItem type — the shared two-row +
   * icon-slot layout (partner-notes row chrome). Row 1: icon (or select
   * checkbox) + title (+ Continued chip for session continuations + star
   * marker); row 2: subtitle / match snippet. Right meta: relative date that
   * swaps to a chevron on hover, plus a hover-revealed kebab. The 3-column grid
   * rides `.session-card--iconed` (never the base `.session-card`, shared by
   * six pages). Cards carry `data-item-type` + `data-item-id`; the click
   * handler dispatches on the (type, id) pair.
   * @param {Object} item - SearchItem
   * @param {Object} [opts]
   * @param {string|null} [opts.activeId] - Session ID currently shown in the right panel
   * @param {string|null} [opts.activeKey] - `itemKey` of the note/guide currently
   *   shown in the right panel. Sessions match on `activeId` (the session id,
   *   which is not the SearchItem id); note/guide match on the type-qualified key.
   * @param {string} [opts.query] - Lowercased search query (title bolding + snippet)
   * @param {boolean} [opts.selectMode] - Render the select checkbox instead of the icon
   * @param {boolean} [opts.selected] - Whether this card is selected
   * @returns {string} HTML string
   */
  static renderItemCard(item, { activeId = null, activeKey = null, query = '', selectMode = false, selected = false } = {}) {
    const note = item.type === 'session' ? item.payload : null;
    const sessionId = note ? (note.session?.id || note.sessionId) : null;
    const sessionNumber = note ? (note.session?.sessionNumber ?? '?') : null;
    const isActive = item.type === 'session'
      ? Boolean(activeId) && sessionId === activeId
      : Boolean(activeKey) && activeKey === SessionsSection.itemKey(item);
    const activeCls = isActive ? ' session-card--active' : '';
    const selectedCls = selectMode && selected ? ' session-card--active note-row--selected' : '';

    // Archived items stay inline (no separate view) and are de-emphasized.
    const archivedCls = item.archived ? ' session-card--archived' : '';

    const continuedFrom = note && typeof note._continuedFromTitle === 'string' ? note._continuedFromTitle : null;
    const continuedChip = continuedFrom ? '<span class="session-card__chip">Continued</span> ' : '';
    const titleHtml = SessionsSection.highlightMatch(item.title, query);
    const star = item.starred
      ? `<span class="note-row__star" aria-label="Starred">${STAR_SVG}</span>`
      : '';

    // Unread indicator: session reads `item.flagged` (manual-only "flag to
    // revisit" toggle — a chat is never "new" to the person who was in it, so
    // sessions don't get the automatic viewed-driven dot). Note/guide read
    // `item.viewed` — note additionally gated to received (a sent note never
    // carries a real viewedAt from the sender's perspective, so without this
    // gate every sent note would show a permanent false-positive dot/pill —
    // see searchSources.normalizeMemo). No new kebab item here — /search's
    // dot is purely passive (see the CONTENT_VIEWED/CONTENT_FLAGGED
    // subscriptions in `initialize()`).
    const isReceivedNote = item.type === 'note' && item.payload?.direction === 'received';
    const isFreshNoteOrGuide = (item.type === 'guide' || isReceivedNote) && !item.viewed;
    const showDot = item.type === 'session' ? Boolean(item.flagged) : isFreshNoteOrGuide;
    const dotHtml = showDot ? '<span class="session-card__type-icon-dot" aria-hidden="true"></span>' : '';
    const isWithdrawnSentNote = item.type === 'note'
      && item.payload?.direction === 'sent'
      && Boolean(item.payload?.revokedAt);
    let statusIndicator = '';
    if (isWithdrawnSentNote) {
      statusIndicator = '<span class="notification-label notification-label--gray">withdrawn</span>';
    } else if (item.archived) {
      statusIndicator = '<span class="notification-label notification-label--gray">archived</span>';
    } else if (isFreshNoteOrGuide) {
      const label = isReceivedNote ? 'unread' : 'new';
      statusIndicator = `<span class="notification-label notification-label--blue">${label}</span>`;
    }

    const snippetHtml = query ? SessionsSection.buildSnippet(item, query) : '';
    const subtitleHtml = snippetHtml
      ? `<p class="session-card__summary">${snippetHtml}</p>`
      : (item.subtitle ? `<p class="session-card__summary">${SessionsSection.escapeHtml(item.subtitle)}</p>` : '');

    // A guide with path artwork leads with its cover page (guideCoverHtml —
    // the same treatment the sidebar and dashboard give it); every other row,
    // and a guide without artwork, renders its sprite glyph. Both sit inside the
    // same type-icon wrapper so the unread dot and the archived mute apply
    // alike.
    const iconId = iconIdForItem(item);
    const iconSize = iconSizeForItem(item);
    const glyphHtml = guideCoverHtml(item)
      || `<svg width="${iconSize}" height="${iconSize}"><use href="#icon-${iconId}"/></svg>`;
    const iconHtml = `<span class="session-card__type-icon" aria-hidden="true">${glyphHtml}${dotHtml}</span>`;
    const slot = selectMode
      ? `<input type="checkbox" class="note-row__checkbox" tabindex="-1" aria-hidden="true"${selected ? ' checked' : ''}>`
      : iconHtml;

    const relDate = formatRelativeDate(item.date);
    const options = selectMode ? '' : SessionsSection.renderRowOptions(item);
    const sessionNumberAttr = note ? ` data-session-number="${sessionNumber}"` : '';

    return `
      <div class="card session-card session-card--iconed note-row${activeCls}${selectedCls}${archivedCls}"
           data-item-type="${SessionsSection.escapeHtml(item.type)}"
           data-item-id="${SessionsSection.escapeHtml(String(item.id))}"${sessionNumberAttr}
           role="button"
           tabindex="0"
           ${selectMode ? `aria-pressed="${selected}"` : `aria-expanded="${Boolean(isActive)}"`}>
        <span class="session-card__slot">${slot}</span>
        <div class="session-card__content">
          <div class="session-card__title-row">
            ${star}
            <h4 class="session-card__title">${continuedChip}${titleHtml}</h4>
          </div>
          ${subtitleHtml}
        </div>
        <span class="note-row__meta">
          ${statusIndicator
            ? `<span class="note-row__status">${statusIndicator}</span>`
            : (relDate ? `<span class="note-row__date">${SessionsSection.escapeHtml(relDate)}</span>` : '')}
          ${options}
          <span class="session-card__chevron note-row__chevron" aria-hidden="true">${CHEVRON_SVG}</span>
        </span>
      </div>
    `;
  }

  /**
   * Render a single session card from a RAW session note (normalizes then
   * delegates). Retained as a thin static entry for callers/tests that hold a
   * raw note; the /search stream itself renders normalized SearchItems.
   * @param {Object} note - Session note object
   * @param {string|null} [activeId] - Session ID currently shown in the right panel
   * @returns {string} HTML string
   */
  static renderSessionCard(note, activeId = null) {
    return SessionsSection.renderItemCard(normalizeSessionNote(note), { activeId });
  }

  /**
   * Render the hover-revealed kebab options button + its dropdown menu for one
   * card (mirrors PartnerNotesSection.renderRowOptions). Items are Star/Unstar
   * (omitted for sent notes), Archive/Unarchive (label from state), Delete
   * (danger, omitted for guides — see `buildContentRowMenuItems`). Reuses the
   * shared options-dropdown classes (dropdowns.css).
   * @param {Object} item - SearchItem
   * @returns {string} HTML string
   */
  static renderRowOptions(item) {
    const key = SessionsSection.itemKey(item);
    const isSentNote = item.type === 'note' && item.payload?.direction === 'sent';
    // A guide is never deleted from any surface — Archive is the whole
    // put-it-away vocabulary for an immutable written guide.
    const isGuide = item.type === 'guide';
    const items = [];

    // Discuss: sessions always ("Discuss session"), notes only when received
    // (sent notes have no counterpart conversation to seed) — auto-sorted to
    // the top by renderMenuItems, same as the /notes row kebab. Guides are
    // deliberately absent: a guide is discussed one atom at a time from its
    // own reading panel, never as a whole document.
    if (item.type === 'session' || (item.type === 'note' && !isSentNote)) {
      items.push({ action: 'row-discuss', label: 'Discuss' });
    }

    if (!isSentNote) {
      items.push({ action: 'row-star', label: item.starred ? 'Unstar' : 'Star', icon: item.starred ? 'star' : 'star-outline' });
    }

    // Sessions and notes archive freely (label follows the item's state).
    items.push({ action: 'row-archive', label: item.archived ? 'Unarchive' : 'Archive' });
    if (!isGuide) items.push({ action: 'row-delete', label: 'Delete', variant: 'danger' });

    return renderRowKebab({ items, dataAttrs: { 'data-item-key': key } });
  }

  /**
   * Truncate text to specified length
   * @param {string} text - Text to truncate
   * @param {number} maxLength - Maximum length
   * @returns {string} Truncated text
   */
  truncateText(text, maxLength) {
    if (!text || text.length <= maxLength) return text || '';
    return text.substring(0, maxLength - 3) + '...';
  }

  /**
   * Attach event listeners
   */
  attachEventListeners() {
    this.attachSessionsEventListeners();
  }

  /**
   * Attach event listeners for sessions tab
   */
  attachSessionsEventListeners() {
    // Search input
    const searchInput = this.container.querySelector('.insights-search__input');
    if (searchInput) {
      searchInput.addEventListener('input', (e) => this.handleSessionSearch(e.target.value));
    }

    // Search clear button
    const clearBtn = this.container.querySelector('[data-action="clear-session-search"]');
    if (clearBtn) {
      clearBtn.addEventListener('click', () => this.clearSessionSearch());
    }

    // Session-type + sort dropdowns are owned by ResultsFilterBar
    // (mounted in _mountFilterBar / handled in _handleFilterChange).

    // Retry button (for the all-sources-failed error state) — re-runs the
    // full federated loadData, then rebuilds the page shell (the error state
    // has no cards container to partially update).
    const retryBtn = this.container.querySelector('[data-action="retry-sessions"]');
    if (retryBtn) {
      retryBtn.addEventListener('click', async () => {
        await this.loadData();
        if (this.isActive) this.render();
      });
    }

    // Delegated data-action handler (select-mode toolbar + row kebab + menu
    // items). Attached once on the section container — survives list re-renders.
    this.container.addEventListener('click', this.handleActionClick);

    // Right-click a session card to open its kebab menu (same as the sidebar).
    // Stable arrow ref (class field) — addEventListener only dedupes identical
    // function references, and attachEventListeners() can re-run across
    // activate/deactivate cycles, so an inline closure here would accumulate
    // duplicate listeners over repeat visits.
    this.container.addEventListener('contextmenu', this.handleRowContextMenu);

    // Item card clicks (event delegation, dispatched on the (type, id) pair).
    const cardsContainer = this.container.querySelector('.session-cards-container');
    if (cardsContainer) {
      cardsContainer.addEventListener('click', (e) => {
        // Clicks inside the kebab or its menu, or on the checkbox, must not open
        // the detail (they are handled by handleActionClick / selection).
        if (e.target.closest('[data-action]') || e.target.closest('.note-row__actions')) return;
        const card = e.target.closest('.session-card');
        if (!card) return;
        const { itemType, itemId } = card.dataset;
        if (!itemType || !itemId) return;
        if (this.selectMode) {
          this._toggleSelected(itemType, itemId);
        } else {
          this.handleItemCardClick(itemType, itemId);
        }
      });
    }
  }

  /**
   * Right-click a session card to open its kebab menu (same as the sidebar).
   * @param {MouseEvent} event
   */
  handleRowContextMenu = (event) => {
    this._rowMenu.handleContextMenu(event);
  };

  /**
   * Delegated handler for select-mode toolbar buttons + per-row kebab / menu
   * items. Bound once; guards on `this.container` containment.
   * @param {MouseEvent} event
   */
  handleActionClick = (event) => {
    const target = event.target.closest('[data-action]');
    if (!target || !this.container.contains(target)) return;
    const action = target.getAttribute('data-action');
    const key = target.getAttribute('data-item-key');

    switch (action) {
      case 'row-options':
        event.stopPropagation();
        this._rowMenu.toggle(target);
        break;
      case 'row-discuss':
        event.stopPropagation();
        this._rowMenu.close();
        if (key) this._handleRowDiscuss(key);
        break;
      case 'row-star':
        event.stopPropagation();
        this._rowMenu.close();
        if (key) this._runBulk('star', [key]);
        break;
      case 'row-archive':
        event.stopPropagation();
        this._rowMenu.close();
        if (key) this._runBulk('archive', [key]);
        break;
      case 'row-delete':
        event.stopPropagation();
        this._rowMenu.close();
        if (key) this._runBulk('delete', [key]);
        break;
      case 'select-star':
        this._runBulk('star', [...this.selectedKeys]);
        break;
      case 'select-archive':
        this._runBulk('archive', [...this.selectedKeys]);
        break;
      case 'select-delete':
        this._runBulk('delete', [...this.selectedKeys]);
        break;
      case 'clear-filters':
        // Same action as the filter menu's "Reset filters" row — one code path.
        // The search box is NOT cleared here: it isn't part of the bar, doesn't
        // tint the filter icon, and has its own × affordance.
        this.filterBar?.reset();
        break;
      case 'select-cancel':
        this._exitSelectMode();
        break;
      default:
        break;
    }
  };

  /**
   * Row-kebab Discuss: a session launches "Discuss session" (the same
   * ADVICE hand-off as the detail panel's primary button); a received note
   * launches the same hand-off as the note-detail header's "Discuss Note"
   * button. Sent notes and guides never offer this action (see
   * `renderRowOptions`).
   * @param {string} key - "type:id" selection key
   */
  _handleRowDiscuss(key) {
    const item = this.items.find(it => SessionsSection.itemKey(it) === key);
    if (!item) return;
    if (item.type === 'session') {
      SessionsSection.discussSession(item.payload);
    } else if (item.type === 'note') {
      const note = item.payload;
      launchMemoDiscussAdvice({ memoId: note.id, senderName: note.partnerName || 'your partner' });
    }
  }

  // ===== SELECT MODE =====

  /**
   * The currently-selected SearchItems (resolved from the merged stream).
   * @returns {Object[]}
   */
  _selectedItems() {
    return this.items.filter(item => this.selectedKeys.has(SessionsSection.itemKey(item)));
  }

  /** Clear select-mode state without re-rendering (callers re-render). */
  _resetSelectMode() {
    this.selectMode = false;
    this.selectedKeys = new Set();
  }

  /** Enter multi-select mode: swap the bar for the toolbar, re-render rows. */
  _enterSelectMode() {
    if (this.selectMode) return;
    this.selectMode = true;
    this.selectedKeys = new Set();
    this._mountFilterBar();
    this.updateSessionsList();
  }

  /** Exit multi-select mode: clear selection, restore the filter bar. */
  _exitSelectMode() {
    this._resetSelectMode();
    this._mountFilterBar();
    this.applySessionFilters();
  }

  /**
   * Toggle one card in the selection and refresh toolbar + rows.
   * @param {string} type - SearchItem type
   * @param {string} id - SearchItem id
   */
  _toggleSelected(type, id) {
    const key = `${type}:${id}`;
    if (this.selectedKeys.has(key)) this.selectedKeys.delete(key);
    else this.selectedKeys.add(key);
    this._renderSelectToolbar();
    this.updateSessionsList();
  }

  // ===== ACTION FAN-OUT =====

  /**
   * Run a bulk action (star | archive | delete) over a set of selection keys,
   * fanning out per type (session-notes / partner-notes) with
   * per-type id mapping. `Promise.allSettled` isolates failures; local state is
   * updated only from the fulfilled subset, then the list is re-derived.
   * Delete confirms first; star/archive don't. Failures bucket by error code
   * into a human notice rendered in the cards region.
   *
   * Delete drops guides from the batch before confirming (no guide surface
   * offers Delete — Archive is the whole put-it-away vocabulary for an
   * immutable written guide) and the confirm says so when the selection was
   * mixed. A guides-only Delete does nothing but say why.
   * @param {'star'|'archive'|'delete'} action
   * @param {string[]} keys - "type:id" selection keys
   */
  async _runBulk(action, keys) {
    if (this._actionInFlight || keys.length === 0) return;
    const keySet = new Set(keys);
    let items = this.items.filter(it => keySet.has(SessionsSection.itemKey(it)));
    if (items.length === 0) return;

    if (action === 'delete') {
      const skippedGuides = items.filter(it => it.type === 'guide').length;
      items = items.filter(it => it.type !== 'guide');
      if (items.length === 0) {
        this.actionNotice = SessionsSection.GUIDES_NOT_DELETABLE_NOTICE;
        this.updateSessionsList();
        return;
      }
      const confirmed = await modalService.confirm(
        SessionsSection.deleteConfirmConfig(items.length, skippedGuides)
      );
      if (!confirmed || !this.isActive) return;
    }

    this._actionInFlight = true;
    if (this.selectMode) this._renderSelectToolbar();
    this.actionNotice = '';

    const byType = { session: [], note: [], guide: [] };
    for (const it of items) if (byType[it.type]) byType[it.type].push(it);

    const errors = [];   // { code, count }
    const removed = new Set(); // itemKeys removed (delete)
    // Direction applied by an archive fan-out, captured from `_fanArchive`'s
    // return. It CANNOT be re-derived here: `_envelopeJob` flips `it.archived`
    // in place on the same object references `items` holds, so by this point
    // the array is uniform in the new direction and re-running
    // `!items.every(it => it.archived)` yields the inverse.
    let archivedDir = null;

    try {
      if (action === 'star') {
        await this._fanStar(byType, items, errors);
      } else if (action === 'archive') {
        archivedDir = await this._fanArchive(byType, items, errors);
      } else if (action === 'delete') {
        await this._fanDelete(byType, removed, errors);
      }
    } finally {
      this._actionInFlight = false;
    }
    // Remove deleted rows from the merged stream + broadcast — BEFORE the
    // `!isActive` guard (R2-Warning-1) so other surfaces still sync when the
    // user navigated away mid-flight. Remove locally BEFORE emitting so this
    // section's own `content:deleted` listener self-skips (finds nothing to
    // remove → bails without a redundant second close). Star/archive emit
    // inside `_envelopeJob`, also before this guard. Guide events are not a
    // special case: the sidebar and the `/insights` guides tab both listen on
    // `guide`, keyed by the same guideDefinitionId.
    let panelRemoved = false;
    if (removed.size > 0) {
      const panelKey = this.currentSessionNote
        ? `session:${this.currentSessionNote.id}`
        : (this.currentDetailNote ? SessionsSection.itemKey(this.currentDetailNote) : null);
      panelRemoved = !!panelKey && removed.has(panelKey);
      const removedItems = this.items.filter(it => removed.has(SessionsSection.itemKey(it)));
      this.items = this.items.filter(it => !removed.has(SessionsSection.itemKey(it)));
      removedItems.forEach(it => emitContentDeleted(it.type, it.id));
    }

    if (!this.isActive) return;

    // Close the panel if the open item was deleted here. Kept behind the
    // `!isActive` guard (review fix #4 — an inactive /search must not close
    // another section's panel). The self-delete listener bailed, so this is
    // the single close for a self-originated delete.
    if (panelRemoved && this._panelOpen) eventBus.emit('right-panel:close');

    this.actionNotice = this._buildActionNotice(errors);

    // Success toast only on a clean archive batch — a partial failure is
    // already named in the inline notice, and pairing it with "3 items
    // archived" would contradict it. Mixed-type batches pass `null` so the
    // copy falls back to "item(s)".
    if (action === 'archive' && errors.length === 0 && archivedDir !== null) {
      const types = new Set(items.map(it => it.type));
      notifyArchived({
        count: items.length,
        type: types.size === 1 ? items[0].type : null,
        archived: archivedDir
      });
    }

    if (this.selectMode) {
      this._exitSelectMode();
    } else {
      this.applySessionFilters();
    }
  }

  /**
   * Bulk star/unstar fan-out. Stars unless every selected item is already
   * starred (then unstars). Sent notes can't be starred (server is
   * recipient-only) — excluded and named in the notice.
   */
  async _fanStar(byType, items, errors) {
    const starred = !items.every(it => it.starred);
    const jobs = [];

    if (byType.session.length) {
      const ids = byType.session.map(it => it.id);
      jobs.push(this._envelopeJob(API.starSessionNotes(ids, starred), byType.session, { starred }, errors));
    }
    if (byType.note.length) {
      const starrable = byType.note.filter(it => it.payload?.direction !== 'sent');
      const sent = byType.note.length - starrable.length;
      if (sent > 0) errors.push({ code: 'NOTE_SENT_NOT_STARRABLE', count: sent });
      if (starrable.length) {
        const ids = starrable.map(it => it.id);
        jobs.push(this._envelopeJob(discoveryApi.starMemos(ids, starred), starrable, { starred }, errors));
      }
    }
    if (byType.guide.length) {
      const ids = byType.guide.map(it => it.id); // id IS the guideDefinitionId for guide SearchItems
      jobs.push(this._envelopeJob(API.starGuides(ids, starred), byType.guide, { starred }, errors));
    }
    await Promise.allSettled(jobs);
  }

  /**
   * Bulk archive/unarchive fan-out. Direction toggles on the selection: unless
   * every item is already archived (then unarchive), the batch archives — there
   * is no Active/Archived view axis, so the item state drives direction.
   *
   * Returns the direction so `_runBulk` can word its success toast. The caller
   * cannot re-derive it: `_envelopeJob` flips `it.archived` in place on the
   * same objects, so after the await the selection is uniform in the NEW
   * direction and recomputing yields the inverse.
   *
   * @returns {Promise<boolean>} the archived direction applied
   */
  async _fanArchive(byType, items, errors) {
    const archived = !items.every(it => it.archived);
    const jobs = [];

    if (byType.session.length) {
      const ids = byType.session.map(it => it.id);
      jobs.push(this._envelopeJob(API.archiveSessionNotes(ids, archived), byType.session, { archived }, errors));
    }
    if (byType.note.length) {
      const ids = byType.note.map(it => it.id);
      jobs.push(this._envelopeJob(discoveryApi.archiveMemos(ids, archived), byType.note, { archived }, errors));
    }
    if (byType.guide.length) {
      const ids = byType.guide.map(it => it.id);
      jobs.push(this._envelopeJob(API.archiveGuides(ids, archived), byType.guide, { archived }, errors));
    }
    await Promise.allSettled(jobs);
    return archived;
  }

  /**
   * Bulk delete fan-out (per-id for sessions; one call for notes). A 404 is
   * treated as success (already gone). Active sessions can't be deleted
   * (CANNOT_DELETE_ACTIVE) — the row stays and is named in the notice.
   *
   * Guides are NOT a bucket here: no guide surface offers Delete, and
   * `_runBulk` drops them from a mixed selection before this runs (saying so
   * in the confirm), so a guide can never reach this fan-out.
   */
  async _fanDelete(byType, removed, errors) {
    const jobs = [];

    for (const it of byType.session) {
      const sessionId = it.payload?.session?.id || it.payload?.sessionId;
      jobs.push(API.deleteSession(sessionId).then((res) => {
        if (res?.success === false && res?.error?.code && res.error.code !== 'SESSION_NOT_FOUND') {
          errors.push({ code: res.error.code, count: 1 });
        } else {
          removed.add(SessionsSection.itemKey(it));
        }
      }).catch(() => { errors.push({ code: 'SESSION_DELETE_FAILED', count: 1 }); }));
    }
    if (byType.note.length) {
      const ids = byType.note.map(it => it.id);
      jobs.push(discoveryApi.deleteMemos(ids).then(() => {
        for (const it of byType.note) removed.add(SessionsSection.itemKey(it));
      }).catch(() => { errors.push({ code: 'NOTE_DELETE_FAILED', count: byType.note.length }); }));
    }
    await Promise.allSettled(jobs);
  }

  /**
   * Await an envelope-returning bulk call; on success flip the local flag on
   * each affected item, on an error envelope or thrown error record a generic
   * bucket in `errors` so the failure surfaces in the action notice.
   * @param {Promise<Object>} promise - API call returning `{count}` or error envelope
   * @param {Object[]} affected - Items to flip on success
   * @param {{starred?: boolean, archived?: boolean}} patch - Flag(s) to set
   * @param {{code: string, count: number}[]} [errors] - Failure sink
   */
  async _envelopeJob(promise, affected, patch, errors) {
    try {
      const res = await promise;
      // A falsy/null envelope is a swallowed failure (e.g. a network error the
      // API layer resolved to null) — never patch or broadcast a mutation the
      // server didn't confirm (R2-Critical-1).
      if (!res || res.success === false) {
        errors?.push({ code: res?.error?.code || 'BULK_UPDATE_FAILED', count: affected.length });
        return;
      }
      // Apply-local-FIRST, then emit per item, so this section's own listener
      // self-skips (the row already matches).
      for (const it of affected) {
        if (patch.starred !== undefined) {
          it.starred = patch.starred;
          if (it.payload) it.payload.starredAt = patch.starred ? new Date().toISOString() : null;
          emitContentStarred(it.type, it.id, patch.starred);
        }
        if (patch.archived !== undefined) {
          it.archived = patch.archived;
          if (it.payload) it.payload.archivedAt = patch.archived ? new Date().toISOString() : null;
          emitContentArchived(it.type, it.id, patch.archived);
        }
      }
    } catch {
      errors?.push({ code: 'BULK_UPDATE_FAILED', count: affected.length });
    }
  }

  /**
   * Build a human failure notice from bucketed error codes ('' when none).
   * @param {{code: string, count: number}[]} errors
   * @returns {string}
   */
  _buildActionNotice(errors) {
    if (!errors.length) return '';
    const totals = {};
    for (const e of errors) totals[e.code] = (totals[e.code] || 0) + e.count;
    const COPY = {
      CANNOT_DELETE_ACTIVE: (n) => `${n} ${n === 1 ? 'session is' : 'sessions are'} still active and can’t be deleted`,
      NOTE_SENT_NOT_STARRABLE: (n) => `${n} sent ${n === 1 ? 'note' : 'notes'} can’t be starred`
    };
    return Object.entries(totals).map(([code, n]) =>
      COPY[code] ? COPY[code](n) : `${n} ${n === 1 ? 'item' : 'items'} couldn’t be updated`
    ).join('. ') + '.';
  }

  /**
   * Handle search input for sessions
   * @param {string} query - Search query
   */
  handleSessionSearch(query) {
    this.sessionSearchQuery = query.toLowerCase();
    this.applySessionFilters();
    this.updateClearButton();
  }

  /**
   * The current search-box placeholder, derived from the active content filter.
   * @returns {string}
   */
  _searchPlaceholder() {
    return SessionsSection.SEARCH_PLACEHOLDERS[this.contentType]
      || SessionsSection.SEARCH_PLACEHOLDERS.all;
  }

  /**
   * Sync the live search input's placeholder to the active content filter.
   * No-op when the input isn't mounted (loading/error states).
   */
  _updateSearchPlaceholder() {
    const input = this.container.querySelector('.insights-search__input');
    if (input) input.placeholder = this._searchPlaceholder();
  }

  /**
   * Apply the content-type filter + search query to the merged stream.
   * An item matches when any searchField contains the query
   * (case-insensitive substring).
   */
  applySessionFilters() {
    // Keep the placeholder in step with the content filter — whether the user
    // chose it or it was autoselected on entry (both routes land here).
    this._updateSearchPlaceholder();

    const typeFilter = this.contentType === 'all'
      ? null
      : SessionsSection.CONTENT_TYPE_MAP[this.contentType] || null;

    this.filteredItems = this.items.filter(item => {
      // Archived items are NOT hidden — they stay in the stream (styled
      // de-emphasized with an "Archived" pill); only the star filter and the
      // type/query filters narrow the list now.
      if (this.starredFilter && !item.starred) return false;
      if (typeFilter && item.type !== typeFilter) return false;
      if (!this.sessionSearchQuery) return true;
      return (item.searchFields || []).some(field =>
        String(field || '').toLowerCase().includes(this.sessionSearchQuery)
      );
    });

    this.applySessionSort();
    this.updateSessionsList();
    if (this.filterBar) {
      this.filterBar.setCount(this.filteredItems.length);
    }
  }

  /**
   * Clear session search
   */
  clearSessionSearch() {
    this.sessionSearchQuery = '';
    const searchInput = this.container.querySelector('.insights-search__input');
    if (searchInput) {
      searchInput.value = '';
    }
    this.applySessionFilters();
    this.updateClearButton();
  }

  /**
   * Update clear button visibility
   */
  updateClearButton() {
    const clearButton = this.container.querySelector('.insights-search__clear');
    if (clearButton) {
      if (this.sessionSearchQuery) {
        clearButton.classList.add('insights-search__clear--visible');
      } else {
        clearButton.classList.remove('insights-search__clear--visible');
      }
    }
  }

  /**
   * Sort the filtered stream newest-first via `item.date` (sort is fixed).
   * Equal-timestamp ties keep concat order (stable): sessions, then notes.
   */
  applySessionSort() {
    this.filteredItems.sort((a, b) => new Date(b.date) - new Date(a.date));
  }

  /**
   * Update the item list without full re-render (a full render() rebuilds the
   * search input and loses focus)
   */
  updateSessionsList() {
    this._rowMenu.close();
    const cardsContainer = this.container.querySelector('.session-cards-container');
    if (cardsContainer) {
      // Container-agnostic select-mode modifier (content-row.css), shared with
      // /notes.
      cardsContainer.classList.toggle('content-rows--select', this.selectMode);
      cardsContainer.innerHTML = this.renderSessionCardsList();
      // Re-attach card click listeners (event delegation handles this)
    }

    // Update results bar count via the shared component
    if (this.filterBar) {
      this.filterBar.setCount(this.filteredItems.length);
    }
  }

  /**
   * Dispatch a stream card click to its per-type open behavior. Items are
   * matched by the (type, id) pair — ids can collide across sources.
   * @param {string} type - SearchItem type from data-item-type
   * @param {string} id - SearchItem id from data-item-id
   */
  handleItemCardClick(type, id) {
    const item = this.items.find(i => i.type === type && String(i.id) === String(id));
    if (!item) return;

    if (type === 'session') {
      this.handleSessionCardClick(item);
      return;
    }

    if (type === 'note') {
      this.handleNoteCardClick(item);
      return;
    }

    if (type === 'guide') {
      this.handleGuideCardClick(item);
    }
  }

  /**
   * Handle session card click — push (or replace) the URL; the route handler
   * picks up the change via `applyURLParams` and opens the panel. Built from
   * the current URL so `?type=` is preserved.
   *
   * Push when the panel is closed (creates one history entry for "panel
   * opened"). Replace when the panel is already open and the user is swapping
   * to a different session (keeps history flat — back exits the panel
   * regardless of how many sessions were browsed).
   *
   * @param {Object} item - Session SearchItem
   */
  handleSessionCardClick(item) {
    const sessionNumber = item.payload?.session?.sessionNumber;
    if (!Number.isFinite(sessionNumber)) return;

    // Toggle: re-clicking the card already open dismisses the panel, same as
    // its × (the `right-panel:closed` handler owns the URL scrub). Also serves
    // as the dedup guard — page.js doesn't dedup identical navigations and
    // would otherwise push a redundant history entry.
    if (this._panelOpen
        && this.currentSessionNote?.session?.sessionNumber === sessionNumber) {
      eventBus.emit('right-panel:close');
      return;
    }

    const url = new URL(window.location.href);
    url.searchParams.set('session', String(sessionNumber));
    url.searchParams.delete('note');   // mutually exclusive detail params
    url.searchParams.delete('guide');
    const target = url.pathname + url.search;
    if (this._panelOpen) {
      page.replace(target);
    } else {
      page(target);
    }
  }

  /**
   * Handle note card click — push (or replace) `?note=<id>` on the current URL;
   * the route handler picks it up via `applyURLParams` and opens the panel
   * in-place (no navigation to /notes). Mirrors handleSessionCardClick:
   * push when closed, replace when swapping within an open panel. `?session=`
   * and `?guide=` are dropped (the three detail params are mutually exclusive).
   * @param {Object} item - Note SearchItem
   */
  handleNoteCardClick(item) {
    // Toggle / dedup — see handleSessionCardClick.
    if (this._panelOpen
        && this.currentDetailNote
        && String(this.currentDetailNote.id) === String(item.id)) {
      eventBus.emit('right-panel:close');
      return;
    }

    const url = new URL(window.location.href);
    url.searchParams.set('note', String(item.id));
    url.searchParams.delete('session');   // mutually exclusive detail params
    url.searchParams.delete('guide');
    const target = url.pathname + url.search;
    if (this._panelOpen) {
      page.replace(target);
    } else {
      page(target);
    }
  }

  /**
   * Handle guide card click — hand the item to `openContentItem`, which sends
   * a guide to its native surface, `/library`'s Guides tab.
   *
   * Unlike a session or a note, /search does NOT open a guide itself. A guide
   * can have two halves — the reader's own `UserGuide` and the couple's
   * shared reading — and only `/library` holds both manifests; a /search-local
   * reader could fetch the individual half alone, and did, so the same guide
   * read one click differently depending on where it was opened from. One
   * surface, one reader. The cross-page panel handoff keeps the panel open
   * through the navigation (`router.js`'s PANEL_ROUTED_PARAMS).
   * @param {Object} item - Guide SearchItem (`id` is the guideDefinitionId)
   */
  handleGuideCardClick(item) {
    openContentItem(item);
  }

  /**
   * Open the right panel with detail content for a session.
   * @param {Object} sessionNote - Session note object from API
   */
  showDetailView(sessionNote) {
    this.currentSessionNote = sessionNote;
    this.currentDetailNote = null;   // mutual exclusion with the note panel
    this.markSessionNoteViewed(sessionNote);
    this.clearSessionUnread(sessionNote);
    this._panelOpen = true;
    this.updateSessionsList();
    eventBus.emit('right-panel:open', SessionsSection.buildDetailPayload(sessionNote));
  }

  /**
   * Persist the viewed timestamp for a session note. Items are matched by the
   * (type, id) pair — never bare id — and the stamp lands on the session
   * item's payload (the raw note).
   * @param {Object} sessionNote - Session note object from API
   */
  async markSessionNoteViewed(sessionNote) {
    const noteId = sessionNote?.id;
    if (!noteId) return;

    const updated = await API.markSessionNoteViewed(noteId);
    if (!updated) return;
    emitContentViewed('session', noteId, true);

    const applyViewedAt = item =>
      item.type === 'session' && item.id === noteId
        ? { ...item, payload: { ...item.payload, viewedAt: updated.viewedAt } }
        : item;
    this.items = this.items.map(applyViewedAt);
    this.filteredItems = this.filteredItems.map(applyViewedAt);
    if (this.currentSessionNote?.id === noteId) {
      this.currentSessionNote = { ...this.currentSessionNote, viewedAt: updated.viewedAt };
    }
  }

  /**
   * Opening a manually unread session is its read event. Sessions retain the
   * dedicated `flaggedAt` persistence field because `viewedAt` auto-stamps on
   * open, but the user-facing lifecycle matches notes, guides, and practices:
   * clear only after the server confirms, then broadcast so /search and both
   * sidebar sections update together.
   * @param {Object} sessionNote - Session note object from API
   * @returns {Promise<boolean>} Whether an unread marker was cleared
   */
  async clearSessionUnread(sessionNote) {
    const noteId = sessionNote?.id;
    if (!noteId) return false;

    const item = this.items.find(
      candidate => candidate.type === 'session' && String(candidate.id) === String(noteId)
    );
    if (!sessionNote.flaggedAt && !item?.flagged) return false;

    const updated = await API.flagSessionNote(noteId, false).catch((err) => {
      console.error('[SessionsSection] clear session unread failed:', err);
      return null;
    });
    if (!updated || updated.success === false) return false;

    sessionNote.flaggedAt = null;
    emitContentFlagged('session', noteId, false);
    return true;
  }

  // ===== PARTNER-NOTE DETAIL (in-panel, no navigation to /notes) =====

  /**
   * Open the note-detail panel for a routed `?note=<id>`. The note must already
   * be in the loaded stream (it's rendered as a card); a stale/foreign id is
   * scrubbed and no-ops, mirroring the invalid-session guard.
   * @param {string} id - Partner-note (PartnerMemo) id from the URL
   */
  showNoteByIdFromParam(id) {
    const item = this.items.find(it => it.type === 'note' && String(it.id) === String(id));
    if (!item) {
      const url = new URL(window.location.href);
      url.searchParams.delete('note');
      page.replace(url.pathname + (url.search ? url.search : ''));
      return;
    }
    this.showNoteDetailView(item);
  }

  /**
   * Open the right panel with a partner note's detail (self-contained — Discuss,
   * Retract, Star/Archive/Delete all work here). Clears any open session/guide
   * detail (all three are mutually exclusive) and marks a received note viewed.
   * @param {Object} item - Note SearchItem
   */
  showNoteDetailView(item) {
    this.currentSessionNote = null;   // mutual exclusion with the session panel
    this.currentDetailNote = item;
    this._markNoteViewed(item);
    this._panelOpen = true;
    this.updateSessionsList();
    eventBus.emit('right-panel:open', this._buildNotePanelPayload(item));
  }

  /**
   * Build the note-detail right-panel payload via the shared renderer
   * (partnerNotesDrillHelper) — the same one /notes uses — wired with
   * /search's own callbacks. Star/Archive/Delete route through `_runBulk`
   * (the exact list-bar/kebab path); Retract and Discuss are portable.
   * @param {Object} item - Note SearchItem
   * @returns {Object} right-panel payload
   */
  _buildNotePanelPayload(item) {
    const note = item.payload;
    const list = note.direction === 'sent' ? 'sent' : 'received';
    const partnerName = note.partnerName || 'your partner';
    const user = window.app?.currentUser;
    const ownName = user?.firstName || user?.name || user?.username || 'you';
    return buildNoteDetailPayload(note, {
      list,
      partnerName,
      ownName,
      contentType: 'note-detail',
      onDiscuss: () => launchMemoDiscussAdvice({ memoId: note.id, senderName: partnerName }),
      onStar: (next) => this._toggleNoteStar(item, next),
      onRetract: () => this._retractNoteFromPanel(item),
      onOption: (action) => this._handleNotePanelOption(action, item)
    });
  }

  /**
   * Header star toggle for the open note panel. Routes through the shared
   * `_runBulk('star', …)` path (the same one list-bar/kebab use — the locked
   * one-path constraint) and reports success by whether the item's flag
   * actually reached `next`, so RightPanelManager reverts the optimistic flip
   * on failure. (`_runBulk` records failures in actionNotice rather than
   * throwing, so success is read from real item state.)
   * @param {Object} item - The note SearchItem shown in the panel
   * @param {boolean} next - Desired starred state
   * @returns {Promise<boolean>} whether the toggle succeeded
   */
  async _toggleNoteStar(item, next) {
    const key = SessionsSection.itemKey(item);
    await this._runBulk('star', [key]);
    const fresh = this.items.find(it => SessionsSection.itemKey(it) === key);
    return !!fresh && fresh.starred === next;
  }

  /**
   * Opening a received note is the "read" event: stamp `viewedAt` locally and
   * broadcast `content:viewed` (sidebar Recents dot) once the server confirms
   * — not optimistically, matching the "never broadcast an unconfirmed
   * mutation" rule. Mirrors PartnerNotesSection.showDetailView. Called
   * without `await` from the caller (fire-and-forget at the call site, but
   * internally gated on a confirmed response, same shape as
   * `markSessionNoteViewed`).
   * @param {Object} item - Note SearchItem
   */
  async _markNoteViewed(item) {
    const note = item.payload;
    if (note.direction !== 'received' || note.viewedAt) return;
    try {
      await discoveryApi.markMemoViewed(note.id);
    } catch (err) {
      console.error('[SessionsSection] markMemoViewed failed:', err?.error || err);
      return;
    }
    note.viewedAt = new Date().toISOString();
    // Item IS the note's own SearchItem here (found by (type,id) — see
    // showNoteByIdFromParam/showNoteDetailView) — the value is `true`, not
    // `false`: this path only ever transitions to "now viewed".
    item.viewed = true;
    emitContentViewed('note', note.id, true);
    // The caller's own synchronous render (showNoteDetailView's
    // updateSessionsList) already ran before this confirmation landed — force
    // a re-render so the row's unread state actually clears, unless the user
    // has already navigated away from this section.
    if (this.isActive) this.updateSessionsList();
  }

  /**
   * Run a note-panel Options action (archive | delete) through the shared
   * `_runBulk` path — the SAME code the list-bar select-mode and row kebab use
   * — keyed on this note. (Star is the header button, wired separately via
   * `_toggleNoteStar`.) Delete closes the panel via `_runBulk`'s removed-item
   * handling; archive re-emits the rebuilt payload from ACTUAL item state (a
   * failed action leaves the label unchanged — `_runBulk` records failures in
   * actionNotice rather than throwing).
   * @param {'archive'|'delete'} action - Selected option
   * @param {Object} item - The note shown in the panel
   */
  async _handleNotePanelOption(action, item) {
    const key = SessionsSection.itemKey(item);
    await this._runBulk(action, [key]);
    if (action === 'delete') return;   // panel closed by _runBulk if the row went
    if (this._panelOpen
        && this.currentDetailNote
        && SessionsSection.itemKey(this.currentDetailNote) === key) {
      const fresh = this.items.find(it => SessionsSection.itemKey(it) === key);
      if (fresh) {
        this.currentDetailNote = fresh;
        eventBus.emit('right-panel:open', this._buildNotePanelPayload(fresh));
      }
    }
  }

  /**
   * Revoke a sent note from its open panel (the shared helper's `onRetract`).
   * Revoked sent notes are excluded from search, so on success remove the item
   * from the stream, close the panel, and re-derive the list. A 409 (gone
   * server-side) is terminal — same cleanup, resolve so the confirm buttons
   * stay disabled. Other failures rethrow so the helper re-enables them.
   * @param {Object} item - The sent-note SearchItem
   * @returns {Promise<void>}
   */
  async _retractNoteFromPanel(item) {
    const note = item.payload;
    const key = SessionsSection.itemKey(item);
    try {
      await discoveryApi.revokeMemo(note.id);
      if (!this.isActive) return;
      this.items = this.items.filter(it => SessionsSection.itemKey(it) !== key);
      window.notificationManager?.showSuccess('Note withdrawn');
      this.applySessionFilters();
      eventBus.emit('right-panel:close');
    } catch (err) {
      if (!this.isActive) return;
      if (err?.status === 409) {
        window.notificationManager?.showError('This note is no longer available.');
        this.items = this.items.filter(it => SessionsSection.itemKey(it) !== key);
        this.applySessionFilters();
        eventBus.emit('right-panel:close');
        return;
      }
      console.error('[SessionsSection] Retract failed:', err?.error || err);
      window.notificationManager?.showError('Failed to withdraw note. Please try again.');
      throw err;
    }
  }

  /**
   * The renderable moments of a session's journey (`notesJson.sessionJourney`,
   * `[{ label, description }]`): array-guarded, and a moment with neither a
   * label nor a description is dropped rather than rendered as an empty
   * numbered block. One reader for the zone renderer and the Contents menu,
   * so the two can never disagree about whether the zone exists.
   * @param {Object} notesJson - `sessionNote.notesJson`
   * @returns {Array<{label: string, description: string}>}
   */
  static sessionJourneyMoments(notesJson) {
    const journey = Array.isArray(notesJson?.sessionJourney) ? notesJson.sessionJourney : [];
    return journey
      .map(m => ({ label: String(m?.label || '').trim(), description: String(m?.description || '').trim() }))
      .filter(m => m.label || m.description);
  }

  /**
   * Build the panel-header Contents menu for a session detail from the zones
   * the page actually renders, in page order. Null below two zones (the
   * shared `buildContentsMenu` rule — one zone needs no navigation). Rows
   * carry no ordinal: the zones are named, not numbered.
   * @param {Object} sessionNote - Session note from API
   * @returns {Object|null} `headerMenu` payload for RightPanelManager, or null
   */
  static buildDetailContentsMenu(sessionNote) {
    const rows = [];
    const summary = sessionNote?.notesJson?.summary;
    const hasSummary = typeof summary === 'string'
      ? summary.trim() !== ''
      : !!(summary?.text || summary?.keyTakeaway);
    if (hasSummary) {
      rows.push({ id: 'sd-summary', label: 'Summary' });
    }
    if (SessionsSection.sessionJourneyMoments(sessionNote?.notesJson).length > 0) {
      rows.push({ id: 'sd-journey', label: 'How it unfolded' });
    }
    if ((sessionNote?.observations || []).length > 0) rows.push({ id: 'sd-observations', label: 'Observations' });
    if ((sessionNote?.practices || []).length > 0) rows.push({ id: 'sd-practices', label: 'Experiments' });
    if ((sessionNote?.notes || []).length > 0) {
      rows.push({ id: 'sd-notes', label: SessionsSection.notesZoneLabel(sessionNote) });
    }
    if ((sessionNote?.openThreads || []).length > 0) rows.push({ id: 'sd-explore', label: 'Threads to pick up' });
    return buildContentsMenu(rows, { activeSelector: '.session-detail-content [data-sd-zone]' });
  }

  /**
   * Render the session detail content area: title, date, meta row (mode pill,
   * transcript-link count pill, zone jump pills), summary plate, then the
   * zones — journey, observations, experiments, threads.
   * @param {Object} sessionNote - Session note object from API
   * @returns {string} HTML string
   */
  static renderDetailContent(sessionNote) {
    const notesJson = sessionNote.notesJson || {};
    const session = sessionNote.session || {};
    const themes = sessionNote.themes || [];
    const observations = sessionNote.observations || [];
    const practices = sessionNote.practices || [];
    const notes = sessionNote.notes || [];
    const openThreads = sessionNote.openThreads || [];

    // Title
    const title = notesJson.title || themes[0] || 'Session Notes';

    // Date
    const endedAt = session.endedAt || sessionNote.createdAt;
    const dateStr = new Date(endedAt).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });

    // Metadata stats
    const messageCount = session.messageCount || 0;
    const observationCount = observations.length;
    const practicesCount = practices.length;
    const notesCount = notes.length;
    const exploreCount = openThreads.length;

    // The date leads the pill row as a plain fact (the guide masthead's
    // "Written …" pill form), so the masthead is the title plus one row.
    const metaItems = [`<span class="sd-metadata__text">${this.escapeHtml(dateStr)}</span>`];
    if (messageCount > 0) {
      metaItems.push(`<span class="sd-metadata__text"><b>${messageCount}</b> ${messageCount === 1 ? 'message' : 'messages'}</span>`);
    }
    if (observationCount > 0) {
      metaItems.push(`<a class="sd-metadata__text" data-scroll-to="sd-observations"><b>${observationCount}</b> ${observationCount === 1 ? 'observation' : 'observations'}</a>`);
    }
    if (practicesCount > 0) {
      metaItems.push(`<a class="sd-metadata__text" data-scroll-to="sd-practices"><b>${practicesCount}</b> ${practicesCount === 1 ? 'experiment' : 'experiments'}</a>`);
    }
    if (notesCount > 0) {
      metaItems.push(`<a class="sd-metadata__text" data-scroll-to="sd-notes"><b>${notesCount}</b> ${notesCount === 1 ? 'note' : 'notes'}</a>`);
    }
    if (exploreCount > 0) {
      metaItems.push(`<a class="sd-metadata__text" data-scroll-to="sd-explore"><b>${exploreCount}</b> ${exploreCount === 1 ? 'Open Thread' : 'Open Threads'}</a>`);
    }
    const metadataHtml = metaItems.length ? `<div class="sd-metadata">${metaItems.join('')}</div>` : '';

    // Every zone is a disclosure (renderDisclosure, components/disclosure.js),
    // expanded by default, keyed on the zone's element id so a meta-row pill
    // or a Contents-menu jump can force it open through `expandDisclosure`.
    // The count chip rides the header and is CSS-hidden while expanded (an
    // expanded list speaks for itself). `zoneWrapHtml` stamps the wrapper
    // both zone behaviours key on — the closing-rule grammar and the pinning.
    const zone = (id, className, label, count, bodyHtml) => zoneWrapHtml(
      renderDisclosure(id, label, bodyHtml, { expanded: true, count }),
      { id, className }
    );

    // Summary — the page's lead content, on open ground. A "Summary" zone
    // header (a disclosure like every other zone) marks it; the recap runs
    // as prose one step above the page's body voice, and the Key Takeaway
    // follows as the callout in its `point` hue (`--callout --hue-point`,
    // components/takeaway.css) in the serif pull-quote register — with no
    // eyebrow to carry the hue, it wears the terracotta as a left rule
    // instead (`--callout-ruled`: "what rides on it"). Recap first, point last
    // (Doug 2026-08-25). Styles: sessions.css `.sd-overview__*`.
    const summary = notesJson.summary;
    const summaryText = typeof summary === 'string' ? summary : summary?.text || '';
    const keyTakeaway = typeof summary === 'object' ? summary?.keyTakeaway : '';

    // "See full transcript" — the running-text link that drills the full
    // conversation into this same panel. Closes the Summary zone (below the
    // takeaway) when there is one; otherwise it follows the meta row.
    const transcriptLinkHtml = (session.id && messageCount > 0) ? `
      <a href="#" class="inline-link sd-transcript-link">
        See full transcript
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true"><path d="M2.5 6H9.5M9.5 6L6.5 3M9.5 6L6.5 9" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/></svg>
      </a>
    ` : '';

    const hasSummaryZone = !!(summaryText || keyTakeaway);
    const summaryPlateHtml = hasSummaryZone ? zone('sd-summary', 'sd-overview', 'Summary', null, `
      ${summaryText ? `<p class="sd-overview__summary">${this.escapeHtml(summaryText)}</p>` : ''}
      ${keyTakeaway ? `<p class="sd-takeaway sd-takeaway--callout sd-takeaway--callout-ruled sd-takeaway--hue-point sd-plate-lede sd-overview__takeaway">${this.escapeHtml(keyTakeaway)}</p>` : ''}
      ${transcriptLinkHtml}
    `) : transcriptLinkHtml;

    // Build pattern name lookup from patternsDetected (enriched by backend with canonical names)
    const patternNameMap = {};
    for (const p of (notesJson.patternsDetected || [])) {
      if (p.pattern_code && p.pattern_name) {
        patternNameMap[p.pattern_code] = p.pattern_name;
      }
    }

    // Session journey — "How it unfolded": the session's moments in order,
    // on a timeline rail. Each moment is a hollow ring on a connector that
    // ends at the last marker, which is filled: the arrival is the one row
    // that differs from the rest (style guide §5 — mark the exception). The
    // label and description take the shared lead/body voices
    // (components/guide-block.css) so the journey and the Observations below
    // read in one type system; only the rail geometry is the journey's own.
    const moments = SessionsSection.sessionJourneyMoments(notesJson);
    const journeyHtml = moments.length ? zone('sd-journey', 'sd-journey', 'How it unfolded', moments.length, `
        <ol class="sd-journey__steps">
          ${moments.map((m, i) => `
            <li class="sd-journey__step${i === moments.length - 1 ? ' sd-journey__step--last' : ''}">
              <span class="sd-journey__marker" aria-hidden="true"></span>
              <div class="sd-journey__content">
                ${m.label ? `<p class="guide-block__lead">${this.escapeHtml(m.label)}</p>` : ''}
                ${m.description ? `<div class="guide-block__body"><p>${this.escapeHtml(m.description)}</p></div>` : ''}
              </div>
            </li>
          `).join('')}
        </ol>
    `) : '';

    // Observations — claim blocks in the same rhythm, list form (hairline per
    // row: a discrete unordered list, not an essay — see sessions.css).
    const observationsHtml = observations.length ? zone('sd-observations', 'sd-observations', 'Observations', observationCount, `
        <div class="sd-blocks sd-blocks--list">
          ${observations.map(obs => this.renderObservationItem(obs, patternNameMap, { discuss: true })).join('')}
        </div>
    `) : '';

    // The three artifact zones below — experiments, notes, threads — are one
    // family: the same nav card (components/navCard.js) in the same list
    // wrapper, differing only in what each row's right rail reports. What the
    // session MADE comes first, in the order it was made; what it LEFT OPEN
    // comes last.
    const artifactList = (cardsHtml) => `<div class="sd-artifact-list">${cardsHtml}</div>`;

    const practicesHtml = practices.length
      ? zone('sd-practices', 'sd-practices', 'Experiments', practicesCount,
        artifactList(practices.map(renderPracticeNavCard).join('')))
      : '';

    // Partner notes written in this session. Sent notes only, so the rail
    // reports when it went — or that it was withdrawn.
    const notesHtml = notes.length
      ? zone('sd-notes', 'sd-notes', SessionsSection.notesZoneLabel(sessionNote), notesCount,
        artifactList(notes.map(renderNoteNavCard).join('')))
      : '';

    // Open threads — matches /discovery topic detail's "Threads to pick up"
    // card shape (renderThreadNavCard, threadDrillHelper.js). A disclosure
    // like the zones above it, although it is terminal: on a page whose
    // every zone is a disclosure, a lone static header would read as a
    // different kind of section rather than the same kind at the end.
    const exploreHtml = openThreads.length ? zone('sd-explore', 'sd-explore', 'Threads to pick up', exploreCount,
      artifactList(openThreads.map(renderThreadNavCard).join(''))) : '';

    return `
      <div class="sd-title-row">
        <h1 class="sd-title">${this.escapeHtml(title)}</h1>
      </div>
      ${metadataHtml}
      ${summaryPlateHtml}
      ${journeyHtml}
      ${observationsHtml}
      ${practicesHtml}
      ${notesHtml}
      ${exploreHtml}
    `;
  }

  /**
   * What the Notes zone is called: "Notes to {partner}" when the partner's name
   * resolved server-side, "Partner notes" when it didn't (an unnamed partner, or
   * a legacy row). Never "Notes" alone — on a page that IS a session note, a
   * zone called "Notes" reads as the page's own contents.
   *
   * One reader for the zone renderer and the Contents menu, so the two can't
   * disagree about what the zone is called.
   *
   * @param {Object} sessionNote - Session note from API
   * @returns {string} Zone label
   */
  static notesZoneLabel(sessionNote) {
    const name = (sessionNote?.notes || []).find(n => n?.partnerName)?.partnerName;
    return name ? `Notes to ${name}` : 'Partner notes';
  }

  /**
   * Render one observation as a claim block (components/guide-block.css):
   * title as the lead, description as the body, and a trailing action line
   * holding "Discuss this" (`data-sd-discuss` — NOT `data-guide-atom`, so the
   * guide reader's delegated handler ignores it) plus "See pattern" when the
   * observation's pattern code resolves to a canonical name. Both are the
   * shared quiet inline action, so an observation's hand-offs look like every
   * other hand-off on a detail page.
   * The markup must sit inside a `.sd-blocks` wrapper (sessions.css), which
   * supplies the gutter the block's mark is positioned into. Two consumers:
   * this page's Observations zone and the /library topic-summary panel
   * (`InsightsSection.renderTopicSummaryPanel`). "Discuss this" is opt-in,
   * because its hand-off is a session continuation — only a page that IS a
   * session can wire it; a consumer that leaves it off must not render a
   * button nothing answers.
   * @param {Object} obs - SessionObservation record
   * @param {Object} patternNameMap - Map of pattern_code to canonical pattern_name
   * @param {Object} [opts]
   * @param {boolean} [opts.discuss=false] - Render the "Discuss this" action
   * @returns {string} HTML string
   */
  static renderObservationItem(obs, patternNameMap, { discuss = false } = {}) {
    const hasPattern = !!(obs.patternCode && patternNameMap[obs.patternCode]);
    const actions = [];
    if (discuss) {
      actions.push(`<button type="button" class="guide-atom__action" data-sd-discuss="${this.escapeHtml(obs.id)}"><span>Discuss this</span>${iconArrowUpRight()}</button>`);
    }
    if (hasPattern) {
      actions.push(`<button type="button" class="guide-atom__action" data-sd-pattern="${this.escapeHtml(obs.patternCode)}"><span>See pattern</span>${iconArrowUpRight()}</button>`);
    }
    const actionsHtml = actions.length ? `<div class="guide-block__actions">${actions.join('')}</div>` : '';

    return `
      <div class="guide-block" data-observation-id="${this.escapeHtml(obs.id)}">
        <p class="guide-block__lead">${this.escapeHtml(obs.title)}</p>
        <div class="guide-block__body"><p>${this.escapeHtml(obs.description)}</p></div>
        ${actionsHtml}
      </div>
    `;
  }

  /**
   * Render an explore next prompt
   * @param {Object} question - ExploreQuestion record
   * @returns {string} HTML string
   */
  static renderExplorePrompt(question) {
    // Prefer the subject-first user-facing headline as the scannable primary
    // line (thread-agent v4); fall back to the full question for legacy rows.
    const primary = question.headline && question.headline.trim()
      ? question.headline.trim()
      : question.question;
    return `
      <div class="sd-explore-prompt" data-explore-id="${question.id}">
        <div class="sd-explore-prompt__content">
          <div class="sd-explore-prompt__question">${this.escapeHtml(primary)}</div>
          ${question.context ? `<p class="sd-explore-prompt__anchor">${this.escapeHtml(question.context)}</p>` : ''}
        </div>
        <svg class="sd-explore-prompt__arrow" width="20" height="20" viewBox="0 0 20 20" fill="none">
          <path d="M7.5 5L12.5 10L7.5 15" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </div>
    `;
  }

  /**
   * Attach event listeners for detail content (meta-row jump pills, the
   * transcript link, observation actions, open-thread cards)
   * @param {HTMLElement} container - Detail content container
   * @param {Object} sessionNote - Session note used to look up observation / open-thread context on click
   */
  static attachDetailEventListeners(container, sessionNote) {
    wireDisclosures(container);
    container.addEventListener('click', async (e) => {
      // Meta-row jump pills — the value is the zone's element id. Scrolled
      // with scrollIntoView (not a scrollTo on this container: the panel's
      // `.right-panel__content` is the scroller) so the zone's
      // `scroll-margin-top` lands it clear of its own pinned header.
      const statLink = e.target.closest('[data-scroll-to]');
      if (statLink) {
        e.preventDefault();
        // The zone's disclosure is keyed on its id, so one op both forces a
        // collapsed zone open and scrolls its wrapper (with its
        // `scroll-margin-top`) into view — guide §1: every expansion, click
        // or jump, goes through `expandDisclosure`.
        expandDisclosure(container, statLink.dataset.scrollTo, { scrollIntoView: true });
        return;
      }

      // Transcript link (the message-count pill) - drill the full
      // conversation into this same panel
      const transcriptLink = e.target.closest('.sd-transcript-link');
      if (transcriptLink) {
        e.preventDefault();
        SessionsSection.openTranscriptInPanel(sessionNote);
        return;
      }

      // Observation "Discuss this" — an ADVICE conversation opened on that
      // one observation rather than the whole session.
      const discussButton = e.target.closest('[data-sd-discuss]');
      if (discussButton) {
        e.preventDefault();
        const obs = sessionNote?.observations?.find(o => String(o.id) === discussButton.dataset.sdDiscuss);
        if (obs) SessionsSection.discussObservation(sessionNote, obs);
        return;
      }

      // Observation "See pattern" — drills the shared pattern detail into
      // this panel (patternDrillHelper.js, the mirror of sessionDrillHelper);
      // the back arrow returns here. Falls back to the /library deep link
      // when the panel cannot drill.
      const patternButton = e.target.closest('[data-sd-pattern]');
      if (patternButton) {
        e.preventDefault();
        openPatternInPanel(patternButton.dataset.sdPattern);
        return;
      }

      // Experiment card clicks — drill into the REAL experiment detail
      // (practiceDrillHelper.js → PracticesSection's own panel), so the
      // experiment reads the same here as on /experiments and the back arrow
      // returns. The row the session note already holds is passed straight in:
      // no second fetch, and nothing depends on /experiments having been
      // visited. Falls back to the /experiments deep link at the drill cap.
      const practiceCard = e.target.closest('[data-practice-id]');
      if (practiceCard) {
        const practice = sessionNote?.practices?.find(
          p => String(p.id) === practiceCard.dataset.practiceId
        );
        if (practice) openPracticeInPanel(practice);
        return;
      }

      // Partner-note card clicks — drill into the shared note-detail panel
      // (partnerNotesDrillHelper.js), the same one /notes and /search open.
      // Every action is wired portably here: this panel is drilled from a page
      // that holds no note list to refresh, so each one reports its own outcome
      // and pops back rather than closing (a close drains the whole drill
      // stack, taking this session note with it).
      const noteCard = e.target.closest('[data-note-id]');
      if (noteCard) {
        const note = sessionNote?.notes?.find(n => String(n.id) === noteCard.dataset.noteId);
        if (note) {
          SessionsSection.openSessionNoteMemoInPanel(note, {
            onChanged: () => SessionsSection.rerenderDetail(container, sessionNote)
          });
        }
        return;
      }

      // Open Thread card clicks — drill into the shared thread-detail panel
      // (threadDrillHelper.js), matching /discovery's topic-detail cards.
      // Only that panel's "Discuss thread" button launches ADVICE chat.
      const threadCard = e.target.closest('[data-thread-id]');
      if (threadCard) {
        const threadId = threadCard.dataset.threadId;
        const question = sessionNote?.openThreads?.find(q => q.id === threadId);
        if (question) {
          // Session detail already knows the source session — stamp it onto
          // the thread so the panel's date pill + "Session N" tag render
          // like every other entry point (topic detail, Threads tab). Not
          // circular: the tag jumps back to this exact session, a normal
          // "return to where I drilled in from" affordance, not a dead end.
          const enrichedQuestion = { ...question, session: question.session || sessionNote.session };
          const payload = buildThreadDetailPayload(enrichedQuestion);
          const panel = window.app?.modules?.rightPanelManager;
          if (panel?.canDrill?.()) {
            eventBus.emit('right-panel:drill', payload);
          } else {
            eventBus.emit('right-panel:open', payload);
          }
        }
      }

    });
  }

  /**
   * Drill a partner note the session produced into this same panel.
   *
   * Static, and its actions call the API directly, because this panel is opened
   * from a session note — a page that holds no note list to patch and may not
   * even be /search. The /search path's handlers are bound to that page's
   * `this.items`; reusing them here would act on a list that isn't there. Each
   * action reports its own outcome and then pops back to the session note:
   * `right-panel:close` drains the whole drill stack, which would close the
   * session note the user drilled in from.
   *
   * Every note here is one the user SENT (a note reaches a session note through
   * its origin session), so the shared payload builder renders the sent variant:
   * Retract as the primary action, no star, no Discuss.
   *
   * @param {Object} note - Sender-view note row from `sessionNote.notes`
   * @returns {void}
   */
  static openSessionNoteMemoInPanel(note, { onChanged = () => {} } = {}) {
    const user = window.app?.currentUser;
    const ownName = user?.firstName || user?.name || user?.username || 'you';

    const popBack = () => eventBus.emit('right-panel:pop');

    const payload = buildNoteDetailPayload(note, {
      list: 'sent',
      partnerName: note.partnerName || 'your partner',
      ownName,
      contentType: 'note-detail',
      onRetract: async () => {
        try {
          await discoveryApi.revokeMemo(note.id);
          note.revokedAt = new Date().toISOString();
          window.notificationManager?.showSuccess('Note withdrawn');
          // A withdrawal has no contentSync event (no surface has ever emitted
          // one), so the zone behind this panel is repainted here — the pop
          // restores the stored node verbatim, and a row still reading "Sent"
          // would contradict the confirmation the user just got.
          onChanged();
          popBack();
        } catch (err) {
          // 409 = already gone server-side: terminal, so resolve (the confirm
          // buttons stay disabled) rather than inviting a retry that can't work.
          if (err?.status === 409) {
            window.notificationManager?.showError('This note is no longer available.');
            popBack();
            return;
          }
          console.error('[SessionsSection] Retract failed:', err?.error || err);
          window.notificationManager?.showError('Failed to withdraw note. Please try again.');
          throw err;
        }
      },
      onOption: async (action) => {
        if (action === 'archive') {
          const archived = !note.archivedAt;
          const result = await discoveryApi.archiveMemos([note.id], archived);
          if (!result || result.success === false) {
            notifyArchiveFailed({ count: 1, type: 'note', archived });
            return;
          }
          note.archivedAt = archived ? new Date().toISOString() : null;
          emitContentArchived('note', note.id, archived);
          notifyArchived({ count: 1, type: 'note', archived });
          popBack();
          return;
        }
        if (action !== 'delete') return;
        const confirmed = await modalService.confirm({
          title: 'Delete note',
          message: 'Permanently delete this note? This cannot be undone.',
          confirmText: 'Delete',
          cancelText: 'Cancel',
          variant: 'danger'
        });
        if (!confirmed) return;
        const result = await discoveryApi.deleteMemos([note.id]);
        if (!result || result.success === false) {
          window.notificationManager?.showError("Couldn't delete that note. Please try again.");
          return;
        }
        emitContentDeleted('note', note.id);
        popBack();
      }
    });

    const panel = window.app?.modules?.rightPanelManager;
    if (panel?.canDrill?.()) {
      eventBus.emit('right-panel:drill', payload);
    } else {
      eventBus.emit('right-panel:open', payload);
    }
  }

  /**
   * Escape HTML to prevent XSS (instance delegate to static).
   * @param {string} text - Text to escape
   * @returns {string} Escaped text
   */
  escapeHtml(text) {
    return escapeHtml(text);
  }

  /**
   * Escape HTML (static — usable from buildDetailPayload and other modules).
   */
  static escapeHtml(text) {
    return escapeHtml(text);
  }

  /**
   * Build the right-panel payload for a session detail view.
   * Static so other sections can drill into a session without needing a SessionsSection instance.
   * @param {Object} sessionNote - Session note from API
   * @returns {Object} payload for `right-panel:open` / `right-panel:drill`
   */
  static buildDetailPayload(sessionNote) {
    const detailContent = document.createElement('div');
    detailContent.className = 'session-detail-content';
    detailContent.innerHTML = SessionsSection.renderDetailContent(sessionNote);
    SessionsSection.attachDetailEventListeners(detailContent, sessionNote);

    const { starButton, optionsDropdown, primaryButton } = SessionsSection.buildSessionActions(sessionNote);

    const payload = {
      title: '',
      titleAlign: 'left',
      starButton,
      optionsDropdown,
      primaryButton,
      // Contents rows for the zones present (null below two — see
      // buildDetailContentsMenu); rows jump via the shared delegated handler.
      headerMenu: SessionsSection.buildDetailContentsMenu(sessionNote),
      content: detailContent,
      contentType: 'session-detail'
    };
    payload.onDispose = SessionsSection.watchArtifactZones(detailContent, sessionNote, payload);
    return payload;
  }

  /**
   * Repaint the detail body from its (mutated) session note. The zones are
   * rendered from the model, so every in-place update goes through here rather
   * than patching a row's DOM — one render path, no second way for the page to
   * be built.
   *
   * @param {HTMLElement} container - The rendered detail content element
   * @param {Object} sessionNote - The session note to render
   * @returns {void}
   */
  static rerenderDetail(container, sessionNote) {
    container.innerHTML = SessionsSection.renderDetailContent(sessionNote);
    wireDisclosures(container);
  }

  /**
   * Keep the artifact zones honest while this page sits behind a drilled panel.
   *
   * An experiment archived or deleted from its drilled detail, or a note
   * archived or removed from its own, pops back to THIS page — which is a
   * payload rendered once and never refetched, so without this it would still
   * show the row as it was. `contentSync` is where those mutations are
   * announced, so the zones follow them, each kind the way its own zone reads:
   *
   *   - A deleted row of either kind leaves the model. There is nothing left to
   *     link to.
   *   - An archived EXPERIMENT stays and re-renders as "Archived" — the zone
   *     lists what the session produced, in whatever state it is now, so an
   *     archive is a state change here rather than a removal (and a restore
   *     from another surface puts it back on track).
   *   - An archived NOTE leaves: an archived note lives on /notes, which is
   *     exactly why the zone never lists one.
   *
   * A withdrawal is the one mutation that never reaches here, because no
   * surface has ever emitted a contentSync event for it; the note drill
   * repaints the zone through `onChanged` instead (`rerenderDetail`, the same
   * repaint this watcher uses).
   *
   * Returns the unsubscribe function for the panel's `onDispose`, so the
   * listeners die with the payload rather than outliving every session note the
   * user opens.
   *
   * @param {HTMLElement} container - The rendered detail content element
   * @param {Object} sessionNote - The session note the element was rendered from
   * @param {Object} [payload] - The panel payload, so the Contents menu can be
   *   rebuilt alongside the body (the panel reads `payload.headerMenu` when it
   *   pops back to this page, so a zone that just disappeared must leave the
   *   menu too — a Contents row that jumps nowhere is a broken row)
   * @returns {Function} Unsubscribe
   */
  static watchArtifactZones(container, sessionNote, payload = null) {
    const zoneKey = (itemType) => (
      itemType === 'practice' ? 'practices' : (itemType === 'note' ? 'notes' : null)
    );

    const rerender = () => {
      SessionsSection.rerenderDetail(container, sessionNote);
      if (payload) payload.headerMenu = SessionsSection.buildDetailContentsMenu(sessionNote);
    };

    const onDeleted = ({ itemType, itemId } = {}) => {
      const key = zoneKey(itemType);
      const rows = key && sessionNote[key];
      if (!itemId || !Array.isArray(rows)) return;
      const next = rows.filter(row => String(row.id) !== String(itemId));
      if (next.length === rows.length) return;
      sessionNote[key] = next;
      rerender();
    };

    const onArchived = ({ itemType, itemId, archived } = {}) => {
      const key = zoneKey(itemType);
      const rows = key && sessionNote[key];
      if (!itemId || !Array.isArray(rows)) return;

      if (itemType === 'note') {
        // Archived notes are not this zone's; an unarchive elsewhere can't put
        // one back here, because the payload only ever held live ones.
        if (!archived) return;
        const next = rows.filter(row => String(row.id) !== String(itemId));
        if (next.length === rows.length) return;
        sessionNote[key] = next;
        rerender();
        return;
      }

      const practice = rows.find(row => String(row.id) === String(itemId));
      if (!practice) return;
      practice.status = archived ? 'archived' : 'active';
      rerender();
    };

    eventBus.on(CONTENT_DELETED, onDeleted);
    eventBus.on(CONTENT_ARCHIVED, onArchived);
    return () => {
      eventBus.off(CONTENT_DELETED, onDeleted);
      eventBus.off(CONTENT_ARCHIVED, onArchived);
    };
  }

  /**
   * The notice shown when a Delete batch was nothing but guides. Guides are
   * never deleted from any surface, so the action can't run — say why rather
   * than failing silently.
   */
  static GUIDES_NOT_DELETABLE_NOTICE = 'Guides can’t be deleted — archive them instead.';

  /**
   * Confirm-modal config for a single/bulk session delete. Shared by
   * `_runBulk`'s delete branch and `confirmAndDeleteSession` so the two
   * delete-confirm call sites can't drift apart.
   * @param {number} [n=1] - Number of items being deleted
   * @param {number} [skippedGuides=0] - Guides dropped from a mixed selection;
   *   when non-zero the message says they stay, so the count the user confirms
   *   and the outcome they get agree
   * @returns {Object} `modalService.confirm()` config
   */
  static deleteConfirmConfig(n = 1, skippedGuides = 0) {
    const kept = skippedGuides > 0
      ? ` ${skippedGuides === 1 ? 'The guide' : `The ${skippedGuides} guides`} in your selection will stay — guides can only be archived.`
      : '';
    return {
      title: `Delete ${n === 1 ? 'item' : `${n} items`}`,
      message: `${n === 1 ? 'This item' : 'These items'} will be removed. This can’t be undone.${kept}`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
      variant: 'danger'
    };
  }

  /**
   * Confirm and delete a single session from its detail panel's Options
   * dropdown. Static (mirrors `buildSessionActions`/`buildDetailPayload`) so
   * it works when drilled to with no live `SessionsSection` instance
   * mounted — it can't reuse the instance method `_runBulk`, which filters
   * against `this.items`.
   * @param {Object} sessionNote - Session note from API
   * @returns {Promise<void>}
   */
  static async confirmAndDeleteSession(sessionNote) {
    const confirmed = await modalService.confirm(SessionsSection.deleteConfirmConfig(1));
    if (!confirmed) return;

    const sessionId = sessionNote.session?.id || sessionNote.sessionId;
    const res = await API.deleteSession(sessionId);
    // API.deleteSession never throws — it swallows network errors and
    // resolves null, so `!res` must be checked explicitly (a bare
    // `res?.success === false` check would treat null as success).
    if (!res || (res.success === false && res.error?.code && res.error.code !== 'SESSION_NOT_FOUND')) {
      const code = res?.error?.code;
      const message = code === 'CANNOT_DELETE_ACTIVE'
        ? "This session is still active and can’t be deleted"
        : 'Failed to delete session. Please try again.';
      window.notificationManager?.showError(message);
      return;
    }

    window.notificationManager?.showSuccess('Session deleted');
    emitContentDeleted('session', sessionNote.id);
    eventBus.emit('right-panel:close');
  }

  /**
   * Build the shared header actions (star toggle + Options dropdown + "Discuss
   * session" button) used by both the session-notes detail and the transcript
   * drill view.
   * @param {Object} sessionNote - Session note from API
   * @returns {{ starButton: Object, optionsDropdown: Object, primaryButton: Object }}
   */
  static buildSessionActions(sessionNote) {
    return {
      starButton: {
        starred: !!sessionNote.starredAt,
        onClick: async (next) => {
          const result = await API.starSessionNotes([sessionNote.id], next);
          // A single-note toggle requires exactly one row to have matched —
          // unlike the bulk /search select-mode action, a count of 0 (foreign
          // or missing id) must be treated as a failure, not a silent no-op.
          const ok = result?.count === 1;
          if (ok) {
            emitContentStarred('session', sessionNote.id, next);
          }
          return ok;
        }
      },
      optionsDropdown: {
        items: [
          { action: 'archive', label: sessionNote.archivedAt ? 'Unarchive' : 'Archive' },
          { action: 'delete', label: 'Delete', variant: 'danger' }
        ],
        onAction: (action) => {
          if (action === 'archive') SessionsSection.toggleArchiveSession(sessionNote);
          else if (action === 'delete') SessionsSection.confirmAndDeleteSession(sessionNote);
        }
      },
      primaryButton: {
        label: 'Discuss session',
        onClick: () => SessionsSection.discussSession(sessionNote)
      }
    };
  }

  /**
   * Launch an ADVICE conversation continuing from a session — "Discuss
   * session". Static so it's callable from both the detail panel's primary
   * button and the /search row-kebab's Discuss action.
   * @param {Object} sessionNote - Session note from API
   */
  static discussSession(sessionNote) {
    const session = sessionNote.session || {};
    const sessionNumber = session.sessionNumber ?? '?';
    const conversationTitle = sessionNote.notesJson?.title || 'this session';
    eventBus.emit('navigate', {
      page: 'chat',
      mode: 'ADVICE',
      force: true,
      autoPrompt: {
        text: `I'd like to continue our discussion from Session ${sessionNumber}: ${conversationTitle}.`,
        source: 'session_continuation',
        sourceSessionIds: [session.id]
      }
    });
  }

  /**
   * Launch an ADVICE conversation picking up ONE observation from a session —
   * the per-observation "Discuss this" action. The same `session_continuation`
   * navigate payload as {@link SessionsSection.discussSession}, with an opener
   * that names the observation, so the continuation context is the session's
   * and the conversation opens on the specific claim.
   * @param {Object} sessionNote - Session note from API
   * @param {Object} obs - SessionObservation record (`title`)
   */
  static discussObservation(sessionNote, obs) {
    const session = sessionNote.session || {};
    const sessionNumber = session.sessionNumber ?? '?';
    const conversationTitle = sessionNote.notesJson?.title || 'this session';
    eventBus.emit('navigate', {
      page: 'chat',
      mode: 'ADVICE',
      force: true,
      autoPrompt: {
        text: `I'd like to pick up something from Session ${sessionNumber}: ${conversationTitle} — "${obs.title}"`,
        source: 'session_continuation',
        sourceSessionIds: [session.id]
      }
    });
  }

  /**
   * Toggle archive/unarchive on a single session from its detail panel's
   * Options dropdown. Static (mirrors `confirmAndDeleteSession`) so it works
   * when drilled to with no live `SessionsSection` instance mounted — it
   * can't reuse `_runBulk`, which filters against `this.items`. Live-patches
   * the dropdown's own label (Archive ↔ Unarchive) via `setOptionsDropdown`
   * on success, and broadcasts via `emitContentArchived` so any open /search
   * list row + its own detail state stay in sync.
   * @param {Object} sessionNote - Session note from API
   * @returns {Promise<void>}
   */
  static async toggleArchiveSession(sessionNote) {
    const next = !sessionNote.archivedAt;
    const result = await API.archiveSessionNotes([sessionNote.id], next);
    // Same exactly-one-row contract as the star toggle above — a count of 0
    // (foreign/missing id) is a failure, not a silent no-op.
    if (result?.count !== 1) {
      window.notificationManager?.showError('Failed to archive session. Please try again.');
      return;
    }

    sessionNote.archivedAt = next ? new Date().toISOString() : null;
    emitContentArchived('session', sessionNote.id, next);
    notifyArchived({ count: 1, type: 'session', archived: next });

    const rightPanelManager = window.app?.modules?.rightPanelManager;
    const { optionsDropdown } = SessionsSection.buildSessionActions(sessionNote);
    rightPanelManager?.setOptionsDropdown(optionsDropdown);
  }

  /**
   * Build the right-panel payload for the read-only chat transcript drill view.
   * The masthead mirrors the session-notes page — `.sd-title`, then a
   * `.sd-metadata` row holding the long date and a plain message-count pill —
   * with the same star / Options / Discuss header actions. The conversation
   * follows directly under a hairline (no zone header), and closes on a
   * "Back to session notes" foot button that pops the panel's drill.
   * @param {Object} sessionNote - Session note from API
   * @param {Array} messages - Message objects ({ role, content, createdAt })
   * @returns {Object} payload for `right-panel:drill`
   */
  static buildTranscriptPayload(sessionNote, messages) {
    const session = sessionNote.session || {};
    const notesJson = sessionNote.notesJson || {};
    const themes = sessionNote.themes || [];

    const title = notesJson.title || themes[0] || 'Session Notes';
    const endedAt = session.endedAt || sessionNote.createdAt;
    const dateStr = new Date(endedAt).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
    const messageCount = session.messageCount || (Array.isArray(messages) ? messages.length : 0);
    const metaItems = [`<span class="sd-metadata__text">${this.escapeHtml(dateStr)}</span>`];
    if (messageCount > 0) {
      metaItems.push(`<span class="sd-metadata__text"><b>${messageCount}</b> ${messageCount === 1 ? 'message' : 'messages'}</span>`);
    }

    const content = document.createElement('div');
    content.className = 'session-detail-content';
    content.innerHTML = `
      <h1 class="sd-title">${this.escapeHtml(title)}</h1>
      <div class="sd-metadata">${metaItems.join('')}</div>
    `;

    if (Array.isArray(messages) && messages.length > 0) {
      content.appendChild(SessionMessageRenderer.render(messages));
    } else {
      const empty = document.createElement('p');
      empty.className = 'transcript-empty';
      empty.textContent = 'This conversation has no messages to display.';
      content.appendChild(empty);
    }

    content.appendChild(SessionsSection.buildTranscriptFoot());

    const { starButton, optionsDropdown, primaryButton } = SessionsSection.buildSessionActions(sessionNote);

    return {
      title: '',
      titleAlign: 'left',
      starButton,
      optionsDropdown,
      primaryButton,
      content,
      contentType: 'transcript'
    };
  }

  /**
   * Build the transcript's foot: a "Back to session notes" button that pops
   * the panel's drill (`right-panel:pop` → `RightPanelManager.pop()`, the
   * same method the drill's auto-wired header back button calls). A button,
   * not an anchor — it does not navigate.
   * @returns {HTMLElement}
   */
  static buildTranscriptFoot() {
    const foot = document.createElement('div');
    foot.className = 'transcript-foot';

    const back = document.createElement('button');
    back.type = 'button';
    back.className = 'inline-link transcript-back';
    back.textContent = 'Back to session notes';
    back.addEventListener('click', () => eventBus.emit('right-panel:pop'));

    foot.appendChild(back);
    return foot;
  }

  /**
   * Load the conversation for a session note and drill its transcript into the
   * current right panel (with an auto-wired back button). No-op if the panel is
   * at its drill-depth cap — transcripts live inside the session-notes panel and
   * have no standalone route.
   * @param {Object} sessionNote - Session note from API (has session.id)
   * @returns {Promise<void>}
   */
  static async openTranscriptInPanel(sessionNote) {
    const session = sessionNote?.session || {};
    if (!session.id) return;

    const rightPanelManager = window.app?.modules?.rightPanelManager;
    if (!rightPanelManager?.canDrill()) {
      console.warn('[SessionsSection] Panel cannot drill — transcript not opened');
      return;
    }

    try {
      const result = await API.getSessionMessages(session.id);
      const messages = result?.messages || [];
      eventBus.emit('right-panel:drill', SessionsSection.buildTranscriptPayload(sessionNote, messages));
    } catch (err) {
      console.error('[SessionsSection] Failed to load transcript:', err);
    }
  }
}
